/* ============================================================================
   FIDÉLITÉ TEXTE ↔ SCÈNE (architecture v2 — mesure avant garde-fou)
   ----------------------------------------------------------------------------
   Détecteur de CONTRADICTIONS entre le texte de l'enseignant et la scène
   routée, TOUTES FAMILLES (invasion, filet, ateliers, parcours…). Chaque
   contrôle extrait une affirmation VÉRIFIABLE du texte (effectif chiffré,
   côté nommé, gardien, conclusion, matériel, cote) et la confronte à la
   scène. Philosophie :
     - CONSERVATEUR : on ne signale que ce qu'on sait extraire sans ambiguïté
       (peu de faux positifs > exhaustivité) — le score est une TENDANCE ;
     - PUR (Node, hors DOM) : consommé par le banc test/eval-fidelity.mjs
       aujourd'hui, réutilisable en GARDE-FOU avant affichage demain ;
     - le MANQUE est une contradiction (joueur/geste décrit absent), le
       SURPLUS rarement (les gabarits ajoutent légitimement files, plots…).
   Renvoie [{ code, msg }] — vide = aucune contradiction détectée (ce qui ne
   prouve pas la perfection : seulement l'absence de défaut détectable).
   ========================================================================== */
import { coreSituationText, invasionSideHints, parseCourtDims, isOneWayFeed } from "./parser.js";
import { qualityIssues } from "./analysis.js";

/* RETENTE CIBLÉE (autocorrection) — message de correction renvoyé à l'IA quand
   la scène routée contredit le texte ET que le garde-fou local n'a pas suffi.
   On lui dit EXACTEMENT quoi corriger (les contradictions détectées), on lui
   rappelle sa réponse précédente, et on le pousse vers le champ STRUCTURÉ
   "acteurs" (le canal de placement fiable). La réponse est une génération
   complète normale : le client la route et ne la GARDE que si elle fait
   STRICTEMENT mieux au détecteur (l'IA propose, le code arbitre). */
export function correctionPrompt(basePrompt, analyse, issues) {
  const list = (issues || []).map((i) => `- ${i.msg}`).join("\n");
  return `${basePrompt}

CORRECTION DEMANDÉE — ta précédente réponse à CE texte a produit un schéma qui le CONTREDIT :
${list}
Relis le texte et réponds à nouveau (JSON complet, même contrat) en corrigeant l'analyse :
- renseigne le champ "acteurs" (poste / cote / repere / distance_but_m / marque) pour CHAQUE joueur que le texte situe — c'est lui qui fixe le placement ;
- vérifie l'effectif, le gardien et la conclusion décrits par le texte ;
- ne change QUE ce qui est contredit, garde le reste identique.
ANALYSE PRÉCÉDENTE (à corriger) : ${JSON.stringify(analyse ?? null)}`;
}

const GOAL_SPORTS = new Set(["football", "futsal", "handball", "basketball", "basket3x3"]);
const INVASION_SPORTS = new Set([...GOAL_SPORTS, "rugby", "ultimate"]);
const NET_SPORTS_F = new Set(["badminton", "tennis", "tennis_de_table", "volleyball"]);
const W = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9, dix: 10 };
const num = (s) => W[String(s).toLowerCase()] || parseInt(s, 10);
const NUMRE = "\\d+|un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix";
/* REPÈRES AU SOL — tout ce qui matérialise l'espace (par opposition au mobile de
   jeu : ballon/disque/volant, à ses rebonds et aux étiquettes de consigne). */
const GROUND_MARKER = /^(?:plot|cone|c[ôo]ne|coupelle|soucoupe|cerceau|tapis|latte|haie|cible|casier|station|panier|boule|cochonnet|temoin|t[ée]moin|marque|zone|agres|agr[èe]s|planche|barre)$/i;

/* AGRÈS NOMMABLES — table PARTAGÉE entre la règle `agres` (qui signale
   l'absence) et le correcteur `addNamedGear` (qui la répare, via
   `missingGear` ci-dessous). Elle vivait DANS la règle : l'y laisser aurait
   obligé le correcteur à la recopier, donc à dériver un jour. */
const AGRES_NOMMES = [
  [/\bpoutres?\b/i, ["poutre"], "poutre"],
  // ⚠️ PAS de `\b` au contact d'une lettre ACCENTUÉE : en JavaScript `\b`
  // ne connaît que l'ASCII [A-Za-z0-9_], donc « incliné, » n'a AUCUNE
  // frontière entre le « é » et la virgule, et « échelle » n'en a aucune
  // à son initiale. Le motif reste sûr : ces expressions sont longues et
  // sans homonyme. (Le dépôt exige une frontière de mot pour toute
  // EXCLUSION ; ici ce sont des déclencheurs, et une frontière ASCII
  // appliquée à du français accentué éteignait la règle en silence.)
  [/\bplans? inclin[ée]s?|\btapis inclin[ée]s?/i, ["plan_incline"], "plan incliné"],
  [/\btremplins?\b/i, ["tremplin"], "tremplin"],
  [/\bplinths?\b|\bchevaux?\b|\bcheval\b/i, ["cheval"], "plinth / cheval"],
  [/\bbarres? fixes?\b/i, ["barre_fixe"], "barre fixe"],
  [/\bbarres? asym[ée]triques?\b/i, ["barres_asym"], "barres asymétriques"],
  [/\bbancs? su[ée]dois\b/i, ["banc"], "banc suédois"],
  [/[ée]chelles? de rythme/i, ["echelle"], "échelle de rythme"],
  [/\bhaies?\b|\blattes?\b/i, ["haie"], "haie / latte"],
  [/\bplanche d['’]appel\b/i, ["planche"], "planche d'appel"],
];

/* ============================================================================
   « 4 × 4 » EST UN DUEL, « 10 × 10 » EST UNE DIMENSION
   ----------------------------------------------------------------------------
   👎 prod du 19/08, et le plus grave du dépôt : « 2 équipes de 4 joueurs, 1 zone
   centrale 10 x 10 est délimitée par 4 plots » sortait à **21 joueurs**
   (« Il y a 20 joueurs sur le terrain au lieu des 2 équipes de 4 ! Inutilisable »).

   DEUX DÉFAUTS CUMULÉS, dans une conversion RECOPIÉE à deux endroits — la règle
   d'effectif au filet et `expectedRoster`, qui alimente les règles d'invasion ET
   le correcteur `completeRoster` :

   1. `(\d)` capturait UN CHIFFRE. « 10 x 10 » se lisait donc « 1 » + « 0 contre
      1 » + « 0 » = « 10 contre 10 ». Et comme le chiffre capturé était le
      PREMIER du second nombre, la dérogation « dimension » (`(?!\s*m)`) ne
      pouvait jamais mordre : ce qui suivait était « 0 m », pas « m ».
   2. Même corrigée, une dimension SANS unité collée — « zone centrale 10 x 10 »,
      « un carré de 5x5 » — restait lue comme un duel.

   Les deux faces du dégât, toutes deux mesurées :
   • le duel FABRIQUÉ gonfle l'effectif attendu, et `completeRoster` ajoute
     jusqu'à douze pions (le cas ci-dessus) ;
   • le duel FABRIQUÉ écrase le vrai : « Terrain réduit 20×15 m. Équipes de 3c3 »
     donnait « 20 contre 15 » -> hors bornes -> a=0, b=0, et le « 3c3 » légitime
     n'était plus lu du tout.

   Le nom d'espace qui PRÉCÈDE est le seul garde-fou qui attrape une dimension
   sans unité. La ponctuation coupe l'association (`[^.;:!?]`) : dans « Terrain
   réduit 20×15 m. Équipes de 3c3 », le point empêche « Terrain » de protéger le
   « 3c3 » qui suit.
   ⚠️ Pas de `\b` après les noms accentués : `\w` de JS ignore les accents, et
   « carré » n'y a donc pas de frontière de mot (piège déjà payé le 13/08).
   Mesuré : 10 prompts sur 1155 changent de lecture, tous dans le bon sens. */
const ESPACE_AVANT = /(?:terrains?|demi.?terrains?|zones?|carr[ée]s?|espaces?|surfaces?|aires?|rectangles?|couloirs?|dimensions?|p[ée]rim[èe]tres?|losanges?|quadrilat[èe]res?)[^.;:!?]{0,24}$/i;
const UNITE_APRES = /^\s*(?:m\b|cm\b|m[èe]tres?\b)/i;
/* « DE CHAQUE CÔTÉ », « dans chaque camp », « de part et d'autre » : le
   MULTIPLICATEUR d'un dispositif à deux buts. Au niveau module — la règle
   `gardien` le lit, et tout futur correcteur devra lire LE MÊME. */
export const CHAQUE_COTE = /(?:de|à) chaque c[ôo]t[ée]|dans chaque camp|aux deux extr[ée]mit[ée]s|de part et d'autre|à chaque but|des deux c[ôo]t[ée]s/i;

export function duelText(t) {
  return String(t || "").replace(
    /(?<![\d.,])(\d{1,2})\s*(?:vs|[cvx×])\s*(\d{1,2})(?!\d)/gi,
    (m0, a, b, off, s) => (ESPACE_AVANT.test(s.slice(Math.max(0, off - 40), off))
      || UNITE_APRES.test(s.slice(off + m0.length)) ? m0 : `${a} contre ${b}`));
}

/* ============================================================================
   COURSES CROISÉES — le déclencheur, la lecture des courses et le test
   d'intersection, AU NIVEAU MODULE.
   ----------------------------------------------------------------------------
   La règle `croisement` (9nonies) et son correcteur `enforceCrossedRuns` les
   partagent LITTÉRALEMENT. Ce dépôt a payé huit fois le désalignement d'un
   correcteur et de sa règle ; une table partagée vit au niveau module, elle ne
   se recopie pas.
   ========================================================================== */
export const ASK_CROSS = /courses? crois[ée]|se croisent|croise (?:derri[èe]re|devant|dans le dos)|croisement/i;
export const CROISEMENT_MSG = "des courses croisées sont demandées mais aucune paire de courses de coéquipiers ne se croise — ÉCHANGER les points d'arrivée des deux coureurs (chacun finit là où l'autre a commencé, ou de son côté opposé) pour que leurs trajectoires se coupent réellement";

/* Les courses de la scène, avec leurs positions ÉVOLUÉES : une course sans
   destinataire déplace son coureur pour les segments suivants. `act` porte
   l'action d'origine, pour que le correcteur puisse la modifier. */
export function crossRuns(scene) {
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const pos = {}, byId = {};
  for (const p of players) { pos[p.id] = { x: +p.x, y: +p.y }; byId[p.id] = p; }
  const runs = [];
  const ordered = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  for (const a of [...ordered, ...actions.filter((x) => x.order == null)]) {
    if (a.type !== "course" && a.type !== "dribble") continue;
    const p = byId[a.from];
    if (!p) continue;
    const A = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : pos[a.from];
    const B = a.to != null ? pos[a.to] : (Number.isFinite(+a.tox) ? { x: +a.tox, y: +a.toy } : null);
    if (A && B && Number.isFinite(A.x) && Number.isFinite(B.x))
      runs.push({ id: a.from, team: p.team || "", A: { ...A }, B: { ...B }, act: a, versJoueur: a.to != null });
    if (a.to == null && B) pos[a.from] = { x: B.x, y: B.y };
  }
  return runs;
}

// Intersection STRICTE de segments (test d'orientation classique) : un simple
// contact d'extrémités ne compte pas comme un croisement.
const ccw = (P, Q, R) => (R.y - P.y) * (Q.x - P.x) - (Q.y - P.y) * (R.x - P.x);
export function segmentsCut(s, u) {
  const d1 = ccw(u.A, u.B, s.A), d2 = ccw(u.A, u.B, s.B), d3 = ccw(s.A, s.B, u.A), d4 = ccw(s.A, s.B, u.B);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/* Une paire de COÉQUIPIERS dont les courses se coupent, ou null. Même équipe
   exigée : couper la route d'un adversaire n'est pas « des courses croisées ». */
export function crossedPair(runs) {
  for (let i = 0; i < runs.length; i++)
    for (let j = i + 1; j < runs.length; j++)
      if (runs[i].id !== runs[j].id && runs[i].team === runs[j].team && segmentsCut(runs[i], runs[j])) return [i, j];
  return null;
}

/* CE QUE LE TEXTE RÉCLAME ET QUE LA SCÈNE N'A PAS — la lecture des trois
   règles de matériel, exposée pour qu'un correcteur unique l'applique.
   Prédicats et dérogations recopiés à l'identique ; 0/null signifie « rien à
   ajouter », jamais « je n'ai pas regardé ». */
export function missingGear(scene, sport, text) {
  const t = coreSituationText(text);
  const markers = Array.isArray(scene?.markers) ? scene.markers : [];
  const out = { agres: [], plots: 0, sol: null };
  if (["gymnastique", "athletisme"].includes(sport)) {
    const poses = new Set(markers.map((m) => (String(m.type || "") === "agres"
      ? String(m.kind || "") : String(m.type || "").toLowerCase())));
    for (const [re, kinds] of AGRES_NOMMES)
      if (re.test(t) && !kinds.some((k) => poses.has(k))) out.agres.push(kinds[0]);
  }
  const pm = t.match(new RegExp(`(${NUMRE})\\s*(?:plots?|c[ôo]nes?|coupelles?)`, "i"));
  const aDesPlots = markers.some((m) => ["plot", "cone", "cône", "coupelle"].includes(String(m.type || "").toLowerCase()));
  if (pm && num(pm[1]) >= 2 && !aDesPlots) out.plots = Math.min(8, num(pm[1]));
  else if (!pm) {
    const hasGroundMarker = markers.some((m) => GROUND_MARKER.test(String(m.type || "").trim()));
    const mm = t.match(/\b(?:install|dispos|plac|pos|mat[ée]rialis|balis|d[ée]limit|jalonn|r[ée]parti|prévoi|prevoi)\w*\s+(?:[^.;:]{0,28}?)\b(plots?|c[ôo]nes?|coupelles?|soucoupes?|cerceaux?|tapis|lattes?|haies?|bandes?|marqueurs?|rep[èe]res? au sol)\b/i);
    if (mm && !/\bsans\s+(?:plot|c[ôo]ne|coupelle|cerceau|tapis|mat[ée]riel)/i.test(t) && !hasGroundMarker) {
      const mot = mm[1].toLowerCase();
      out.sol = /cerceau/.test(mot) ? "cerceau" : /tapis/.test(mot) ? "tapis"
        : /haie|latte/.test(mot) ? "haie" : "plot";
    }
  }
  return out;
}

export function fidelityIssues(scene, text, sport) {
  const issues = [];
  const push = (code, msg) => issues.push({ code, msg });
  const t = coreSituationText(text);
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const markers = Array.isArray(scene?.markers) ? scene.markers : [];
  const zones = Array.isArray(scene?.zones) ? scene.zones : [];
  const nA = players.filter((p) => p.team === "A").length;
  const nB = players.filter((p) => p.team === "B").length;

  // 0) SANTÉ DE BASE — scène sans AUCUNE entité / hors bornes. Une scène sans
  //    action mais avec des joueurs reste LÉGITIME (formation, défense posée,
  //    figure statique) : l'absence des gestes DÉCRITS est couverte par les
  //    contrôles ciblés ci-dessous (conclusion, passes).
  if (!players.length && !markers.length) push("degenere", "scène sans joueur ni matériel");
  const inBounds = (x, y) => x >= 0 && x <= 100 && y >= 0 && y <= 100;
  if (players.some((p) => !inBounds(+p.x, +p.y))
    || actions.some((a) => a.tox != null && !inBounds(+a.tox, +a.toy))) push("bornes", "coordonnées hors terrain");

  // SCÈNE D'ATELIER / DISPOSITIF (rotation, postes, couloirs) : chaque cellule
  // est une mini-scène — les contrôles « de phase de jeu » (effectifs N contre
  // M, gardien, côtés, conclusion, passes) n'y ont pas de sens (« Travail du
  // gardien » y est un NOM d'atelier, pas un gardien à dessiner). Même signal
  // que qualityIssues.
  const isDrill = actions.some((a) => a.type === "rotation")
    || actions.filter((a) => a.type === "tir").length >= 2
    || zones.some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""));

  // 1) EFFECTIF CHIFFRÉ (invasion) — « N contre M », « N joueurs bleus/rouges »,
  //    « N attaquants / M défenseurs ». Seul le MANQUE est une contradiction
  //    (les gabarits ajoutent légitimement files d'attente, vagues…).
  if (INVASION_SPORTS.has(sport) && !isDrill) {
    // « attaque à trois CONTRE UN BLOC médian » n'est pas un duel 3c1 : le
    // 2e nombre suivi d'un mot de SYSTÈME (bloc, défense, zone…) est exclu.
    // « 6x6 » / « 6 X 6 » (MÊME nombre des deux côtés = format d'équipes) vaut
    // « 6 contre 6 » — sauf dimension d'aire (« carré de 10x10 », « 20x40 m »)
    // et sauf N ≤ 3 (« basket 3x3 » est un NOM de discipline, pas un effectif).
    // TROISIÈME copie de la conversion, la dernière (19/08) : elle portait sa
    // PROPRE dérogation de dimension, plus étroite que celle de `duelText` —
    // fenêtre de 16 caractères et nom d'espace COLLÉ au nombre. « 1 zone
    // centrale 10 x 10 est délimitée » lui échappait (« centrale » s'intercale),
    // et le prof lisait « 10 contre 10 » : 4 attaquant(s) dessiné(s) sur une
    // consigne à 4 contre 4. Les tables `ESPACE_AVANT` / `UNITE_APRES` sont
    // désormais celles du module — une seule dérogation pour les trois copies.
    const tDuel = t.replace(/\b(\d|1[01])\s*[xX]\s*(\1)\b/g, (m0, n1, n2, off, s) =>
      (+n1 < 4 || ESPACE_AVANT.test(s.slice(Math.max(0, off - 40), off))
        || UNITE_APRES.test(s.slice(off + m0.length))) ? m0 : `${n1} contre ${n2}`);
    // DUEL DE FORMATIONS (« 4-4-2 contre 4-3-3 », « 442 contre 433 ») : chaque
    // équipe doit compter la SOMME de sa formation (le duel « 2 contre 4 » que
    // formeraient les chiffres adjacents est neutralisé par les gardes ci-dessous).
    const fm = ["football", "futsal"].includes(sport)
      ? t.match(/\b(\d(?:-\d){1,3}|\d{3})\b\s*(?:contre|vs\.?|face [àa]u?n?e?)\s*\b(\d(?:-\d){1,3}|\d{3})\b/i) : null;
    if (fm) {
      const sumF = (s) => s.replace(/-/g, "").split("").reduce((a, d) => a + +d, 0);
      const sA = sumF(fm[1]), sB = sumF(fm[2]);
      if (sA >= 6 && sA <= 10 && sB >= 6 && sB <= 10 && nA + nB > 0) {
        if (nA < sA) push("effectif", `« ${fm[0].trim()} » : ${nA} joueur(s) dessiné(s) côté A (${sA} attendus)`);
        if (nB < sB) push("effectif", `« ${fm[0].trim()} » : ${nB} joueur(s) dessiné(s) côté B (${sB} attendus)`);
      }
    }
    const duel = fm ? null : tDuel.match(new RegExp(`(?<![-\\d])\\b(${NUMRE})\\s*contre\\s*(${NUMRE})\\b(?![-\\d])(?!\\s+(?:bloc|d[ée]fens|zone|rideau|ligne|syst[èe]me))`, "i"));
    if (duel) {
      const a = num(duel[1]), d = num(duel[2]);
      // À l'école du FOOT À 7 (et au hand/futsal), « N contre N » COMPTE le
      // gardien : une équipe à N-1 joueurs de champ + SON gardien dessiné est
      // conforme (salve football S09 : « 7 contre 7 », rouge en 3-2-1 +
      // gardien = 7). Attribution par camp : A défend à gauche (x<50), B à
      // droite. Mesuré sur les archives : les 3 déclenchements historiques
      // (PROD1/64/78, tous des 👎 réels) sont sans gardien ou en surplus —
      // aucun n'est éteint, la tolérance est gratuite.
      const gkTol = new Set(["football", "futsal", "handball"]).has(sport);
      const gkL = players.filter((p) => p.team === "k" && +p.x < 50).length;
      const gkR = players.filter((p) => p.team === "k" && +p.x >= 50).length;
      const okA = nA >= a || (gkTol && nA === a - 1 && gkL > 0);
      const okB = nB >= d || (gkTol && nB === d - 1 && gkR > 0);
      if (a >= 1 && a <= 11 && nA + nB > 0 && !okA) push("effectif", `« ${a} contre ${d} » : ${nA} attaquant(s) dessiné(s)`);
      if (d >= 1 && d <= 11 && nA + nB > 0 && !okB) push("effectif", `« ${a} contre ${d} » : ${nB} défenseur(s) dessiné(s)`);
    }
    const mb = t.match(new RegExp(`(${NUMRE})\\s+(?:joueurs?\\s+)?(?:bleus?|attaquants?)\\b`, "i"));
    // « (deux) ÉQUIPES DE N » (salve nº1, A02 : « deux équipes de 5 » → 2
    //   joueurs dessinés, aucun défenseur). C'est un effectif chiffré comme un
    //   autre, mais il ne dit pas « N contre M » : il échappait aux contrôles
    //   ci-dessus. Les deux camps doivent porter N joueurs de champ.
    //   Il faut le multiplicateur EXPLICITE « DEUX équipes de N » : « Equipe de
    //   4 joueurs » seul décrit la TAILLE d'une équipe dans un aménagement
    //   (souvent une seule est dessinée, face à un défenseur) — l'exiger des
    //   deux camps était un faux positif.
    const em = t.match(new RegExp(`\\b(?:deux|2)\\s+[ée]quipes\\s+de\\s+(${NUMRE})\\b`, "i"));
    if (em && num(em[1]) >= 2 && num(em[1]) <= 11 && nA + nB > 0) {
      const n = num(em[1]);
      if (nA < n) push("effectif", `« ${em[0].trim()} » : ${nA} joueur(s) dessiné(s) d'un côté (${n} attendus)`);
      if (nB < n) push("effectif", `« ${em[0].trim()} » : ${nB} joueur(s) dessiné(s) de l'autre côté (${n} attendus)`);
    }
    if (mb && num(mb[1]) <= 11 && nA < num(mb[1])) push("effectif", `« ${mb[0]} » : ${nA} dessiné(s)`);
    const mr = t.match(new RegExp(`(${NUMRE})\\s+(?:joueurs?\\s+)?(?:rouges?|d[ée]fenseurs?)\\b`, "i"));
    if (mr && num(mr[1]) <= 11 && nB < num(mr[1])) push("effectif", `« ${mr[0]} » : ${nB} dessiné(s)`);
  }

  // 1bis) EFFECTIF CHIFFRÉ (filet) — « N contre M » : chaque camp doit compter
  //       ses joueurs (les multi-terrains dupliquent : seul le MANQUE compte).
  const FILET_SPORTS = new Set(["volleyball", "badminton", "tennis", "tennis_de_table"]);
  if (FILET_SPORTS.has(sport) && !isDrill) {
    // « 4 × 4 », « 3 X 3 », « 4x3 » : le signe × (et le x) valent « contre » —
    // hors dimensions (« 20 × 15 m »). Le lot prod du 06/08 écrivait tous ses
    // effectifs ainsi (« Volley-ball 4 × 4 ») et AUCUN contrôle ne les lisait.
    // Conversion PARTAGÉE avec `expectedRoster` (`duelText`) : elle était
    // recopiée ici, et le défaut l'était avec elle.
    const duel = duelText(t)
      .match(new RegExp(`\\b(${NUMRE})\\s+contre\\s+(${NUMRE})\\b`, "i"));
    if (duel) {
      const n1 = num(duel[1]), n2 = num(duel[2]);
      if (n1 >= 1 && n1 <= 6 && n2 >= 1 && n2 <= 6 && nA + nB > 0
        && (Math.min(nA, nB) < Math.min(n1, n2) || Math.max(nA, nB) < Math.max(n1, n2)))
        push("effectif", `« ${n1} contre ${n2} » : ${nA} joueur(s) d'un côté, ${nB} de l'autre`);
      // SURPLUS — au FILET seulement : un « 4 × 4 » à 5 joueurs de champ d'un
      // côté n'est pas un match à 4 (👎 PROD78 : « les joueurs sont 5 ! »).
      // L'invasion garde sa tolérance (défenseur en retard « +1 », jokers,
      // formations complètes : 12 surplus LÉGITIMES mesurés sur le corpus) ;
      // au filet, la mesure donne ZÉRO surplus légitime sur 161 duels — hors
      // terrains dupliqués (_g2) et rôles d'attente/encadrement, décomptés.
      const multi = players.some((p) => /_g\d/i.test(String(p.id)));
      if (!multi && n1 >= 1 && n1 <= 6 && n2 >= 1 && n2 <= 6) {
        const fieldCount = (team) => players.filter((p) => p.team === team
          && !/attente|observ|arbitre|enseign|coach|remplaç|\blanceur|\bdistributeur/i.test(p.role || "")).length;
        const fA = fieldCount("A"), fB = fieldCount("B");
        if (fA + fB > 0 && Math.max(fA, fB) > Math.max(n1, n2))
          push("effectif", `« ${n1} contre ${n2} » : ${Math.max(fA, fB)} joueurs de champ dessinés d'un côté — dessiner EXACTEMENT ${n1} et ${n2} joueurs (les remplaçants éventuels en « En attente », hors du terrain)`);
      }
    }
    // « EN DOUBLE » est un effectif chiffré qui ne se dit pas en chiffres :
    // deux joueurs de champ PAR CAMP (salve raquettes, R25 : « tennis double :
    // serveur côté droit, partenaire au filet, relanceur en diagonale » → le
    // partenaire du relanceur n'était pas dessiné, la paire adverse n'existait
    // pas). Mots pièges écartés (« double-pas », « double-penalty »,
    // « doubler »), et le simple n'est évidemment pas concerné.
    // « COMPTE DOUBLE » N'EST PAS UN FORMAT (salve badminton nº3, B09 : un
    // match à thème en SIMPLE — « tout point conclu par un smash compte
    // double » — était rejeté pour effectif de double ; même piège sur le 👎
    // archivé PROD110, « un point compte double »). On RETIRE les tournures de
    // SCORE avant de tester le format ; mesuré : 32 vrais doubles conservés,
    // 2 faux positifs éliminés sur le lot doré.
    const sansScore = t.replace(/(?:compte(?:nt)?|vaut|valent|rapporte(?:nt)?|marque(?:nt)?)\s+(?:un\s+point\s+)?doubles?|points?\s+doubles?|score\s+double|double\s+point/gi, " ");
    if (/\bdoubles?\b/i.test(sansScore) && !/double.?pas|double.?p[ée]nalty|doubler|double.?contact/i.test(t)) {
      // ⚠️ « Re-LANCEUR » contient « lanceur » : les exclusions de rôle doivent
      // partir d'une FRONTIÈRE DE MOT, sinon le relanceur d'un double est
      // décompté et la paire adverse semble incomplète (deux faux positifs sur
      // le lot doré avant correction).
      const field = (team) => players.filter((p) => p.team === team
        && !/observ|arbitre|enseign|coach|en attente|\bdistributeur|\blanceur/i.test(p.role || "")).length;
      const dA = field("A"), dB = field("B");
      if (dA + dB > 0 && (dA < 2 || dB < 2))
        push("effectif", `« en double » : ${dA} joueur(s) d'un côté, ${dB} de l'autre — un double se dessine à DEUX par camp (les quatre joueurs, chacun à son poste)`);
    }
    /* EFFECTIF NOMMÉ À UN SEUL CAMP — « deux défenseurs », « trois élèves »,
       « cinq joueurs ». L'INVASION possède ce contrôle depuis longtemps
       (`mb`/`mr` ci-dessus : « N joueurs bleus », « N défenseurs ») ; il n'a
       JAMAIS été branché au FILET, où seul « N contre M » était lu. C'est le
       trou par lequel passait le 👎 LE PLUS RÉPÉTÉ DU DÉPÔT — sept retours
       (PROD23, 59, 106, 127, 143, 166, 181), tous au volley, tous sur le même
       gabarit, tous disant la même chose : « il n'y en a qu'un ! ».

       MESURÉ AVANT ÉCRITURE sur DEUX populations indépendantes : 7
       déclenchements sur les scènes RÉELLEMENT SERVIES du lot prod (7 vrais
       👎, aucun autre) et 4 sur le corpus routé (4 vrais défauts vérifiés un
       par un) — ZÉRO faux positif.

       Les rôles d'ENCADREMENT ne comptent pas : l'enseignant-distributeur
       n'est pas un élève. « En attente » compte, LUI — un élève rangé dans sa
       file est bel et bien dessiné, c'est le dispositif, et l'exclure ferait
       un faux positif sur toutes les colonnes. */
    {
      const un1 = t.match(new RegExp(`\\b(${NUMRE})\\s+(?:jeunes\\s+|joueurs?\\s+)?(?:[ée]l[èe]ves?|joueurs?|d[ée]fenseurs?|receveurs?|r[ée]ceptionneurs?|attaquants?|relanceurs?)\\b`, "i"));
      if (un1) {
        const n = num(un1[1]);
        const field = players.filter((p) => !/observ|arbitre|enseign|coach|remplaç|\blanceur\b|\bdistributeur/i.test(p.role || "")).length;
        if (n >= 2 && n <= 6 && field > 0 && field < n)
          push("effectif", `« ${un1[0].trim()} » : ${field} joueur(s) dessiné(s) — les dessiner TOUS LES ${n}, chacun à son poste sur le terrain (étalés en largeur s'ils jouent ensemble, en colonne s'ils passent chacun leur tour)`);
      }
    }
  }

  // 1ter) EFFECTIF DE GROUPE — « par groupes de N », « rotation à N », « jeu à
  //       N » (N ≥ 3) : au moins N-1 personnages dessinés (un observateur/
  //       gardien manquant est toléré). Attrape les ateliers IA à UNE cellule
  //       qui écrasent un dispositif à plusieurs rôles (scène quasi vide) —
  //       s'applique AUSSI aux scènes d'atelier, c'est leur défaut typique.
  {
    const gm = t.match(new RegExp(`(?:par\\s+(?:groupes?|[ée]quipes?)\\s+de|rotation\\s+[àa])\\s+(${NUMRE})\\b`, "i"));
    if (gm) {
      const n = num(gm[1]);
      const field = players.filter((p) => p.team === "A" || p.team === "B" || p.team === "k").length;
      if (n >= 3 && n <= 8 && field > 0 && field < n - 1)
        push("effectif", `« ${gm[0].trim()} » : ${field} joueur(s) dessiné(s)`);
    }
  }

  // 1quater) SYSTÈME DÉFENSIF NOMMÉ — « face à la zone 2-3 », « défense 6-0 »,
  //          « bloc en 4-4-2 » : la somme des rangées chiffre l'effectif
  //          défensif attendu. Seul le MANQUE est une contradiction.
  if (INVASION_SPORTS.has(sport) && !isDrill) {
    const sm = t.match(/(?:zone|d[ée]fense|bloc)\s+(?:bas\s+|m[ée]dian\s+|haut\s+)?(?:replac[ée]e?\s+)?(?:en\s+)?(\d(?:-\d){1,3})\b/i);
    if (sm) {
      const sum = sm[1].split("-").reduce((s, d) => s + +d, 0);
      if (sum >= 3 && sum <= 11 && nA + nB > 0 && nB < sum)
        push("effectif", `« ${sm[0].trim()} » : ${nB} défenseur(s) dessiné(s) sur ${sum}`);
    }
  }

  // 1quinquies) REPLI COMPTÉ SANS COURSE DE REPLI — « deux défenseurs reculent
  //             en sprint » décrit un SOUS-GROUPE en course : si la scène a
  //             PLUS de défenseurs que le compte mais aucune course défensive
  //             étiquetée repli/retour, la transition n'est pas montrée.
  if (INVASION_SPORTS.has(sport) && !isDrill) {
    const rm = t.match(new RegExp(`(${NUMRE})\\s+(?:d[ée]fenseurs?|rouges?)[^.;\\n]{0,40}(?:recul|se replient|reviennent en sprint)`, "i"));
    if (rm) {
      const n = num(rm[1]);
      const labeled = actions.some((a) => a.type === "course" && /^D/.test(String(a.from || "")) && /repli|revien/i.test(a.label || ""));
      if (n >= 1 && n <= 5 && nB > n && nB >= 3 && !labeled)
        push("repli_compte", `« ${rm[0].trim()} » : aucune course de repli défensif dessinée`);
    }
  }

  // 2) GARDIEN — décrit -> présent ; « sans gardien / but vide » -> absent.
  //    Un gardien dessiné MALGRÉ « sans gardien » est une contradiction MÊME en
  //    atelier (un dispositif « vers mini-but sans gardien » rendu avec gardien
  //    est faux dans tous les cas) ; le sens inverse reste réservé aux phases
  //    de jeu (« Travail du gardien » est un NOM d'atelier).
  if (GOAL_SPORTS.has(sport)) {
    const noGk = /sans gardien|pas de gardien|but vide|petits? buts?|mini.?buts?/i.test(t);
    // MULTI-ATELIERS (salve futsal F16) : « duels 1c1 AVEC gardien ; jeu sur
    // mini-buts SANS gardien » — le texte dit les deux, le gardien dessiné
    // appartient à l'atelier qui en a un : pas de contradiction. Mesuré :
    // 0 texte mixte au corpus, la dérogation est gratuite.
    const avecGk = /avec gardien|face au gardien|gardiens? (?:en place|dans (?:son|le|leur) but)|plus (?:le|un|les) gardiens?|\+ ?gardiens?|duels? [^.;]{0,25}gardien/i.test(t);
    const hasGk = players.some((p) => p.team === "k" || /gardien/i.test(p.role || ""));
    if (noGk && hasGk && !avecGk) push("gardien", "« sans gardien » mais un gardien est dessiné");
    else if (!isDrill && !noGk && /\bgardien(?:ne)?s?\b|portier/i.test(t) && !hasGk) push("gardien", "gardien décrit mais absent");
    // UN GARDIEN « DE CHAQUE CÔTÉ » EN VAUT DEUX (👎 PROD186/187, 19/08).
    // « des attaques simultanées […] DE CHAQUE CÔTÉ il y a un gardien et un
    // défenseur » : l'enseignant décrit DEUX attaques en sens opposés, donc deux
    // buts gardés. La scène servie n'en portait qu'un, deux fois de suite — d'où
    // « toujours le même schéma qui ne tient pas compte de mes précisions ».
    // Le duel « 2 contre 1 » compte UNE attaque ; ce tour de phrase est le
    // MULTIPLICATEUR, et personne ne le lisait. Un seul gardien dessiné est
    // alors une contradiction VÉRIFIABLE, pas une nuance.
    // Mesuré sur les 1162 prompts du corpus : 2 déclenchements, les DEUX sur
    // ces 👎, aucun ailleurs.
    else if (!isDrill && !noGk && CHAQUE_COTE.test(t) && /\bgardien(?:ne)?s?\b|portier/i.test(t)) {
      const nGk = players.filter((p) => p.team === "k" || /gardien/i.test(p.role || "")).length;
      if (nGk === 1) push("gardien", `« ${(t.match(CHAQUE_COTE) || [""])[0]} … gardien » : les DEUX buts sont gardés, un seul gardien est dessiné`);
    }
  }

  // 3) CÔTÉS NOMMÉS (invasion) — « B sur l'aile droite » : le joueur doit être
  //    du bon côté (but à droite : droite = bas / y grand). Extraction partagée
  //    avec le moteur (invasionSideHints, resserrée : directions exclues).
  if (INVASION_SPORTS.has(sport) && nA >= 2 && !isDrill) {
    const hints = invasionSideHints(t, [], Math.min(nA, 11));
    for (const [id, side] of Object.entries(hints)) {
      if (side === "centre") continue;                       // l'axe est retravaillé par le staging
      const p = players.find((pp) => pp.id === id);
      if (!p) continue;                                      // gabarit local à ids P1/P2 : hors périmètre
      if (side === "droite" && p.y < 48) push("cote_joueur", `${id} décrit à DROITE mais dessiné à gauche (y=${p.y})`);
      if (side === "gauche" && p.y > 52) push("cote_joueur", `${id} décrit à GAUCHE mais dessiné à droite (y=${p.y})`);
    }
  }

  // 4) CONCLUSION DÉCRITE — tir au but / essai.
  if (GOAL_SPORTS.has(sport) && !isDrill
    && /tir(?:e|ent)?\s+au\s+(?:but|panier)|frappe\s+au\s+but|marque\s+(?:le|un)\s+(?:but|panier)|tire?\s+[àa]\s+3\s+points|conclu\w*\s+(?:par\s+)?(?:une?\s+)?(?:tir|frappe)/i.test(t)
    && !actions.some((a) => a.type === "tir")) push("conclusion", "tir au but décrit mais aucune flèche de tir");
  if ((sport === "rugby" || sport === "ultimate")
    && /aplatit|marque\s+l['’]essai|r[ée]ceptionne?\s+(?:le\s+disque\s+)?dans\s+(?:la\s+zone|l['’]en.?but)/i.test(t)
    && !markers.some((m) => m.type === "essai") && !actions.some((a) => a.tox != null && a.tox >= 85))
    push("conclusion", "essai/réception en zone décrit mais non dessiné");

  // 5) PASSE DÉCRITE (invasion) — au moins une flèche de passe/centre.
  if (INVASION_SPORTS.has(sport) && !isDrill
    && /fait (?:une|des) passes?|passe [àa] |passe (?:au|vers|pour) |se font des passes/i.test(t)
    && !actions.some((a) => a.type === "passe" || a.type === "centre"))
    push("passe", "passe décrite mais aucune dessinée");

  // 4bis) BACKLINE RUGBY NOMMÉE — « le neuf sert le dix… le quinze
  //       s'intercale » : chaque numéro nommé est un joueur attendu.
  if (sport === "rugby" && !isDrill) {
    const nums = new Set((t.match(/\ble\s+(neuf|dix|onze|douze|treize|quatorze|quinze)\b/gi) || []).map((s) => s.toLowerCase()));
    if (nums.size >= 3 && nA < nums.size)
      push("effectif", `${nums.size} joueurs nommés (le 9, le 10…) : ${nA} attaquant(s) dessiné(s)`);
  }

  // LE JEU AU PIED A LE DROIT D'ALLER VERS L'AVANT. C'est la règle du rugby :
  //   seule la passe À LA MAIN doit partir vers l'arrière ; une chandelle, un
  //   coup de pied à suivre, un dégagement ou un drop s'envoient devant. La
  //   sécurité était jusqu'ici ACCIDENTELLE — l'IA type ses coups de pied en
  //   `renvoi`, que ce contrôle ne regarde pas (vérifié sur les 35 scènes
  //   rugby capturées : « Dégage au pied » et « Chandelle courte » sont des
  //   `renvoi`). Mais le SCENE_PROMPT montre lui-même un
  //   {"type":"passe","label":"passe intérieur du pied"} : le jour où le
  //   modèle applique ce patron au rugby, la règle rejette un geste légal.
  //   Exemption mesurée : 0 changement sur le corpus d'aujourd'hui.
  const JEU_AU_PIED = /\bpied\b|chandelle|\bdrop\b|p[ée]nalit|transformation|d[ée]gagement|grubber|up.?and.?under|coup de pied|\bbott/i;

  // 5bis) PASSE EN AVANT AU RUGBY — INTERDITE : le receveur ne peut pas être
  //       plus avancé (x plus grand, attaque vers la droite) que le passeur AU
  //       MOMENT de la passe. On rejoue le suivi des positions (courses/
  //       conduites déplacent les joueurs) pour comparer à l'instant du geste.
  // (exclu des ATELIERS : un passe-et-suit en couloirs fait circuler la balle
  //  latéralement/vers l'avant — la règle de jeu ne s'y applique pas.)
  if (sport === "rugby" && !isDrill) {
    // MESSAGE PRESCRIPTIF (salve rugby nº2 : 3 rejets passe_avant, 0
    // réparation aboutie — le constat « receveur plus avancé » ne disait pas
    // QUOI corriger, l'IA redessinait le défaut). La consigne dit le geste :
    // reculer la RÉCEPTION derrière le passeur. Cas TOUCHE à part (R10) :
    // l'IA dessine l'alignement en chaîne HORIZONTALE au milieu du terrain —
    // le vrai défaut est la géométrie de l'alignement, le message le dit.
    const touche = /\btouche\b|alignement/i.test(String(text || ""));
    const hint = touche
      ? " — si c'est une TOUCHE : dessiner l'alignement PERPENDICULAIRE à la ligne de touche (colonne de sauteurs à x constant, près du bord y≈8 ou y≈92), le lancer entre dans le terrain, puis chaque passe repart vers l'ARRIÈRE (x égal ou inférieur)"
      : " — RECULER le point de réception : au moment de la passe, le receveur doit être derrière le passeur (x égal ou inférieur), retarder sa course ou aplatir la trajectoire";
    const pos = {};
    for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
    const acts = actions.slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    for (const a of acts) {
      if (a.type === "passe" && a.from && a.to && pos[a.from] && pos[a.to]
        && !JEU_AU_PIED.test(String(a.label || ""))            // le pied a le droit d'aller vers l'AVANT
        && pos[a.to].x > pos[a.from].x + 3)                    // 3u de tolérance (passe latérale)
        push("passe_avant", `passe en avant interdite au rugby (${a.from}→${a.to} : receveur plus avancé)${hint}`);
      if ((a.type === "course" || a.type === "dribble") && a.from && a.tox != null && pos[a.from])
        pos[a.from] = { x: +a.tox, y: +a.toy };               // suivi de position
      if (a.type === "passe" && a.to && a.tox != null) pos[a.to] = { x: +a.tox, y: +a.toy };
    }
  }

  // 5ter) TRANSITION / CONTRE-ATTAQUE — la défense est décrite EN REPLI
  //       (« les défenseurs reculent en sprint », « avant que la défense soit
  //       replacée ») mais la scène montre un bloc INSTALLÉ : tous les
  //       défenseurs de champ postés profond (x ≥ 55, but attaqué à droite)
  //       et AUCUNE course défensive. C'est l'inverse d'une transition.
  //       Conservateur : ≥ 4 défenseurs (un bloc), aucun mouvement défensif.
  if (INVASION_SPORTS.has(sport) && !isDrill
    && /contre[- ]?attaque|transition|mont[ée]e de balle/i.test(t)
    && /d[ée]fenseurs?[^.;\n]{0,40}(?:recul|se replient|revien\w*\s+(?:en\s+)?(?:sprint|d[ée]fendre))|avant\s+(?:que\s+la\s+d[ée]fense[^.;\n]{0,30}(?:replac|en place)|le\s+repli)/i.test(t)) {
    const defs = players.filter((p) => p.team === "B" && !/gardien/i.test(p.role || ""));
    const moved = new Set(actions.filter((a) => ["course", "dribble"].includes(a.type)).map((a) => a.from));
    if (defs.length >= 4 && defs.every((p) => +p.x >= 55) && !defs.some((p) => moved.has(p.id)))
      push("transition", "défense décrite en repli (transition) mais dessinée déjà en place, sans course de repli");
  }

  // 6) MATÉRIEL CHIFFRÉ — « N plots/cônes/coupelles » décrits, aucun dessiné.
  const pm = t.match(new RegExp(`(${NUMRE})\\s*(?:plots?|c[ôo]nes?|coupelles?)`, "i"));
  const hasGroundMarker = markers.some((m) => GROUND_MARKER.test(String(m.type || "").trim()));
  if (pm && num(pm[1]) >= 2
    && !markers.some((m) => ["plot", "cone", "cône", "coupelle"].includes(String(m.type || "").toLowerCase())))
    push("materiel", `« ${pm[0]} » décrits mais aucun plot dessiné`);

  // 6bis) MATÉRIEL À POSER, NON CHIFFRÉ (retours prod 👎 PROD6/PROD8) — le texte
  //    demande d'INSTALLER du matériel au sol (« Installer des plots pour limiter
  //    la zone de départ », « Disposer des tapis ou cerceaux comme cibles ») sans
  //    dire combien : le contrôle 6 ci-dessus, qui exige un nombre, laissait
  //    passer. Un enseignant qui écrit « installer » attend de le VOIR : l'absence
  //    de TOUT repère au sol est une contradiction.
  //    Conservateur : il faut un VERBE DE POSE + un objet au sol dans la même
  //    proposition, et n'importe quel repère au sol dessiné suffit à éteindre le
  //    contrôle (on juge la présence, pas le placement).
  if (!pm) {
    const mm = t.match(/\b(?:install|dispos|plac|pos|mat[ée]rialis|balis|d[ée]limit|jalonn|r[ée]parti|prévoi|prevoi)\w*\s+(?:[^.;:]{0,28}?)\b(plots?|c[ôo]nes?|coupelles?|soucoupes?|cerceaux?|tapis|lattes?|haies?|bandes?|marqueurs?|rep[èe]res? au sol)\b/i);
    if (mm && !/\bsans\s+(?:plot|c[ôo]ne|coupelle|cerceau|tapis|mat[ée]riel)/i.test(t) && !hasGroundMarker)
      push("materiel", `« ${mm[0].trim()} » : matériel à installer décrit mais aucun repère au sol dessiné`);
  }

  // 6ter-bis) PLUSIEURS GARDIENS SUR UN MÊME BUT (salve handball nº1, H18 :
  //    trois postes de tir → l'IA duplique le gardien, trois pastilles jaunes
  //    empilées sur la même cage — il n'y a qu'un but et qu'un gardien).
  //    Hors bandes _gN (chaque mini-terrain a légitimement le sien).
  //    Mesuré avant écriture : 0 déclenchement sur le lot doré.
  if (["handball", "futsal", "football"].includes(sport)) {
    const gks = players.filter((p) => p.team === "k" && !/_g\d/i.test(String(p.id)) && Number.isFinite(+p.x));
    const gauche = gks.filter((p) => +p.x < 50).length, droite = gks.length - gauche;
    if (gauche >= 2 || droite >= 2)
      push("gardiens", `${Math.max(gauche, droite)} gardiens devant le même but — une cage n'a qu'UN gardien : n'en garder qu'un, les autres postes partagent le même`);
  }

  // 6ter) AGRÈS NOMMÉ, NON DESSINÉ (👎 prod 09/08, trois fois : « il n'y a pas
  //    les tapis inclinés ALORS QUE L'ÉLÉMENT EXISTE DANS LA BIBLIOTHÈQUE »,
  //    « pourquoi la poutre n'est pas représentée », « les ateliers sont mal
  //    représentés sans les éléments demandés »). L'enseignant voit ces agrès
  //    dans sa palette : les nommer doit les faire apparaître. Le contrôle 6bis
  //    ci-dessus ne les attrapait pas — il exige un VERBE DE POSE
  //    (« installer », « disposer »), absent de « les élèves traversent une
  //    poutre basse ».
  //    Une ZONE étiquetée « Poutre » ne compte PAS : c'est un rectangle vide,
  //    et c'est précisément ce que l'IA produisait faute de savoir l'agrès
  //    disponible (il n'était pas déclaré dans le SCENE_PROMPT).
  //    Mesuré avant écriture : 3 déclenchements sur les 56 scènes gym/athlé du
  //    lot doré, TOUS des 👎 archivés (PROD41, PROD43, PROD13).
  if (["gymnastique", "athletisme"].includes(sport)) {
    // Un agrès dessiné se reconnaît à son marqueur : `agres` porte son `kind`,
    // les autres sont des types simples.
    const poses = new Set(markers.map((m) => (String(m.type || "") === "agres"
      ? String(m.kind || "") : String(m.type || "").toLowerCase())));
    const absents = AGRES_NOMMES
      .filter(([re, kinds]) => re.test(t) && !kinds.some((k) => poses.has(k)))
      .map(([, , nom]) => nom);
    if (absents.length)
      push("agres", `${absents.length > 1 ? "agrès nommés" : "agrès nommé"} dans le texte mais absent${absents.length > 1 ? "s" : ""} de la scène : ${absents.join(", ")} — poser le marqueur correspondant ({"type":"agres","kind":"…"} ou "haie"/"planche"/"barre"), pas une zone au libellé du même nom : un rectangle étiqueté ne dessine pas l'agrès`);
  }

  // 6quater) ATELIERS TASSÉS DANS UNE BANDE (chantier « grille d'ateliers »,
  //    👎 PROD111 « je ne comprends rien au schéma ! les ateliers demandés
  //    sont… », PROD113 « on ne voit pas l'organisation », PROD117 « les
  //    ateliers sont trop petits et mal représentés ») — quand le texte décrit
  //    N ≥ 3 ateliers, la scène les aligne souvent en RUBAN sur ~30 unités de
  //    haut et laisse les deux tiers du terrain VIDES. Un atelier tassé est un
  //    atelier illisible : l'enseignant ne peut pas y lire son organisation.
  //    On ne juge PAS la disposition (quatre ateliers de lancer CÔTE À CÔTE
  //    sont justes — c'est la sécurité), seulement la SURFACE occupée.
  //    Mesuré avant écriture sur les 8 scènes du corpus à ≥3 zones d'atelier :
  //    3 déclenchements (PROD111 — un 👎 réel — plus deux scènes de la salve
  //    v40), 0 faux positif ; les 5 saines occupent 70 à 96 unités, dont trois
  //    issues du REPLI (`ateliersFromList`, 78-82) — c'est lui qui prend le
  //    relais quand ce contrôle rejette, et il dispose en grille.
  {
    const nAteliers = /(?:\d+|deux|trois|quatre|cinq|six)\s+ateliers|ateliers?\s+(?:en\s+)?(?:circuit|parall)|atelier\s*[1-9A-F]\b/i.test(t);
    const zAt = zones.filter((z) => /atelier|poste|station/i.test(String(z.label || ""))
      && Number.isFinite(+z.y) && Number.isFinite(+z.h));
    if (nAteliers && zAt.length >= 3) {
      const haut = Math.min(...zAt.map((z) => +z.y));
      const bas = Math.max(...zAt.map((z) => +z.y + (+z.h || 0)));
      if (bas - haut < 50)
        push("ateliers_tasses", `les ${zAt.length} ateliers tiennent dans une bande de ${Math.round(bas - haut)} unités (y ${Math.round(haut)} à ${Math.round(bas)}) et laissent le reste du terrain vide — ÉTALER les ateliers sur toute la hauteur : soit les étirer (chacun ~${Math.round(88 / zAt.length)} unités de haut, de y=6 à y=94), soit les disposer en GRILLE à deux colonnes (${Math.ceil(zAt.length / 2)} rangées) ; garder leur ordre et, pour des lancers, leur orientation commune`);
    }
  }

  // 7) DIMENSIONS D'AIRE — données ET aire délimitée décrite -> une cote (📏
  //    ou zone cotée) doit exister.
  const dims = parseCourtDims(t);
  if ((dims.len || dims.wid) && /d[ée]limit|balis|mat[ée]rialis|carr[ée]\s+de|terrain\s+(?:de|r[ée]duit)|couloir\s+de|losange\s+de/i.test(t)) {
    const hasCote = markers.some((m) => m.type === "cote")
      || zones.some((z) => (z.dim && /\d/.test(z.dim)) || /\d+\s*m\b/.test(z.label || ""));
    if (!hasCote) push("cote_aire", "dimensions données mais aire non cotée");
  }

  // 8) DISTANCE AU BUT DU PORTEUR — « a le ballon à N m du but » : le ballon
  //    doit être à la profondeur correspondante (échelle du sport, tolérance
  //    large : c'est une contradiction seulement si on est loin du compte).
  if (INVASION_SPORTS.has(sport)) {
    const dm = t.match(/a le ballon[^.;]{0,40}?[àa]\s+(\d{1,2})\s*m(?:[èe]tres?)?\s+du\s+(?:but|panier)/i);
    const ball = markers.find((m) => ["ballon", "ball", "balle"].includes(String(m.type || "").toLowerCase()));
    if (dm && ball) {
      const SCALE = { handball: 2.8, futsal: 2.35, basketball: 3.3, basket3x3: 3.3 };
      const exp = Math.max(20, Math.min(85, 97 - parseInt(dm[1], 10) * (SCALE[sport] || 0.95)));
      // Le ballon du schéma est figé à sa position de DÉPART : après une
      // relance du gardien, il part du gardien — c'est alors le JOUEUR décrit
      // qui doit être à la distance donnée. On accepte l'un OU l'autre.
      const okBall = Math.abs(ball.x - exp) <= 16;
      const okPlayer = players.some((p) => p.team === "A" && Math.abs(p.x - exp) <= 16);
      if (!okBall && !okPlayer) push("distance", `porteur décrit à ${dm[1]} m du but (x≈${Math.round(exp)}) mais rien ni personne à cette profondeur`);
    }
  }

  // 9bis) TIR HORS DE PORTÉE (retour prod 👎 « les tirs du milieu de terrain
  //    n'ont aucun sens ») — conscient des exercices À DEUX BUTS : quel que
  //    soit le but visé (gauche ou droite), l'ORIGINE du tir (position ÉVOLUÉE
  //    du tireur après ses courses/conduites) doit en être à portée. Seuils
  //    larges pour ne signaler que l'absurde.
  //    + LE CADRE et LE TIR TRONQUÉ (salve handball nº2, 10/08) — sports à cage
  //    CENTRALE (tracés render.js : poteaux y 40-60 sur la ligne de but) : un
  //    tir qui vise la ligne de but doit ARRIVER entre les poteaux (mesuré sur
  //    les scènes archivées : 3 déclenchements prod, TOUS des 👎 réels, dont
  //    « le tir n'est pas vers le but » mot pour mot) ; un tir qui s'arrête en
  //    chemin sans cible décrite est tronqué. Au basket la cible est le panier,
  //    au rugby l'en-but fait toute la largeur : cage exclue. Dérogations : tir
  //    décrit manqué/arrêté/contré, buts matérialisés (plots, mini-buts),
  //    marqueur-cible ou zone à viser à l'arrivée.
  //    Les MESSAGES sont PRESCRIPTIFS : ils sont lus par la RÉPARATION (leçons
  //    croisement 04/08 et profondeur TT 10/08 — en simple constat, l'IA
  //    redessine le défaut : 0/3 réparations sur les tirs longs de la salve
  //    handball avant cette réécriture, le tireur ne bougeait jamais).
  {
    const acts9 = (Array.isArray(scene?.actions) ? scene.actions : [])
      .filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
    const ps9 = Array.isArray(scene?.players) ? scene.players : [];
    const REACH = { handball: 34, futsal: 34, basketball: 34, basket3x3: 34, football: 48, rugby: 48 };
    const CAGE = new Set(["handball", "futsal", "football"]);
    const reach = REACH[sport];
    if (reach) {
      // Dérogations lues sur le texte COMPLET (une variante « tir non cadré »
      // dans les VARIABLES doit éteindre le contrôle, pas le déclencher).
      // Deux familles : des BUTS MATÉRIALISÉS (plots, mini-buts) invalident la
      // géométrie de la cage officielle — les TROIS contrôles s'éteignent
      // (salve handball H16 : l'atelier sur buts inversés en plots était rejeté
      // pour une portée mesurée au but officiel) ; un tir DÉCRIT manqué/arrêté/
      // contré n'éteint que l'arrivée (cadre, tronqué) — sa portée reste due.
      const full = String(text || "");
      const goalsMat = /mini[- ]buts?|multibuts?|buts?[^.;]{0,40}(?:plots?|cônes?|coupelles?|matérialis)|(?:plots?|cônes?|coupelles?)[^.;]{0,40}buts?/i.test(full);
      const tirWaiver = goalsMat || /non cadr|tirs?[^.;]{0,25}(?:manqu[ée]|rat[ée]|contr[ée]|à côté)|à côté du but|au[- ]dessus du but|arrêt du gardien|parade|air ?ball/i.test(full);
      const nearTarget = (x, y) => (Array.isArray(scene?.markers) ? scene.markers : [])
        .some((m) => ["but", "cible", "plot", "cone", "cône", "coupelle", "cerceau"].includes(String(m.type || "").toLowerCase())
          && Math.hypot(+m.x - x, +m.y - y) < 10)
        || (Array.isArray(scene?.zones) ? scene.zones : []).some((z) => /cible|viser|atteindre/i.test(String(z.label || ""))
          && x >= +z.x && x <= +z.x + (+z.w || 0) && y >= +z.y && y <= +z.y + (+z.h || 0));
      const pos9 = {}; for (const p of ps9) pos9[p.id] = { x: +p.x, y: +p.y };
      for (const a of acts9) {
        const from = a.fromx != null ? { x: +a.fromx, y: +a.fromy } : pos9[a.from];
        const to = a.tox != null ? { x: +a.tox, y: +a.toy } : null;
        if (a.type === "tir" && from && to) {
          if (to.x >= 88 || to.x <= 12) {
            const goalX = to.x >= 88 ? 100 : 0;
            if (Math.abs(from.x - goalX) > reach && !goalsMat)
              push("qualite", `tir hors de portée : il part à ${Math.round(Math.abs(from.x - goalX))} unités du but visé (origine x=${Math.round(from.x)}) — faire PRÉCÉDER le tir d'une course d'élan qui amène le tireur à ${goalX === 100 ? `x≥${100 - reach}` : `x≤${reach}`}, et faire partir le tir de l'ARRIVÉE de cette course`);
            else if (CAGE.has(sport) && (to.y < 37 || to.y > 63) && !tirWaiver && !nearTarget(to.x, to.y))
              push("qualite", `tir à côté du cadre : il arrive en (${Math.round(to.x)},${Math.round(to.y)}) alors que la cage occupe y 40 à 60 sur la ligne de but — faire ARRIVER le tir entre les poteaux (${goalX === 100 ? "x≈98" : "x≈2"}, y entre 40 et 60)`);
          } else if (CAGE.has(sport) && !tirWaiver && !nearTarget(to.x, to.y)
            && ((to.x > from.x && to.x > 60) || (to.x < from.x && to.x < 40)))
            push("qualite", `tir interrompu en (${Math.round(to.x)},${Math.round(to.y)}), avant le but : PROLONGER la flèche de tir jusqu'à la cage (${to.x > from.x ? "x≈98" : "x≈2"}, y entre 40 et 60) — ou jusqu'à la cible si le texte en décrit une`);
          // LE PANIER EST UN POINT (salve basket nº2 : « le shooteur du corner »
          // TIRAIT DANS LE CORNER — 4 scènes sur 20, arrivées à 20-36 unités du
          // cercle, aucune vue par la garde puisque le basket est exclu du
          // contrôle de cage). Tout tir doit ARRIVER au cercle (render.js :
          // x≈98,5 · y=50), qu'il soit tronqué ou excentré. Mesuré : 0 faux
          // positif sur les 20 scènes basket archivées des lots prod.
          if ((sport === "basketball" || sport === "basket3x3") && !tirWaiver && !nearTarget(to.x, to.y)) {
            const hoop = (sport === "basket3x3" || to.x > 50) ? { x: 98.5, y: 50 } : { x: 1.5, y: 50 };
            const dH = Math.hypot(to.x - hoop.x, to.y - hoop.y);
            if (dH > 10)
              push("qualite", `tir qui ne va pas au panier : il arrive en (${Math.round(to.x)},${Math.round(to.y)}), à ${Math.round(dH)} unités du cercle — faire ARRIVER la flèche de tir SUR le panier (${hoop.x > 50 ? "x≈98, y=50" : "x≈2, y=50"})`);
          }
        }
        if ((a.type === "course" || a.type === "dribble") && a.from && to && a.to == null) pos9[a.from] = to;
      }
    }
  }

  // 9ter) DÉFENSEUR « ENTRE LES X ET Y MÈTRES » (retour prod 👎 « les défenseurs
  //    sont sur la ligne des 6 mètres, c'est interdit ») — handball/futsal : le
  //    texte place le défenseur DANS la bande [Y m, X m] ; posé SUR la ligne des
  //    X m (ou au-delà), il contredit le texte. Conscient des deux buts.
  if (sport === "handball" || sport === "futsal") {
    const t9 = String(text || "").toLowerCase();
    const bandM = t9.match(/d[ée]fenseurs?[^.;]{0,50}entre (?:les? |la ligne des? )?(\d{1,2})\s*(?:m(?:[èe]tres?)?)?\s*et (?:les? |la ligne des? )?(\d{1,2})\s*m/);
    if (bandM) {
      const near = Math.min(+bandM[1], +bandM[2]), far = Math.max(+bandM[1], +bandM[2]);
      // Profondeur scène : 6 m ≈ 20 unités du but, 9 m ≈ 28 (repères du rendu).
      const u = (m) => Math.round((m / 6) * 20);
      for (const p of (Array.isArray(scene?.players) ? scene.players : [])) {
        if (p.team !== "B" || /gardien/i.test(p.role || "")) continue;
        const dGoal = Math.min(Math.abs(+p.x - 100), Math.abs(+p.x - 0));
        if (dGoal <= u(near) + 0.5)
          push("qualite", `défenseur ${p.id} sur la ligne des ${near} m (voire dedans) alors que le texte le place entre ${near} et ${far} m`);
      }
    }
  }

  // 9quater) COULOIRS « BALISÉS PAR DES CÔNES » NON JALONNÉS (retour prod 👎) :
  //    des couloirs annoncés balisés doivent montrer des LIGNES de plots le long
  //    des séparations — pas un plot isolé par coin.
  {
    const t9 = String(text || "").toLowerCase();
    const zones9 = (Array.isArray(scene?.zones) ? scene.zones : []).filter((z) => /couloir/i.test(z.label || ""));
    if (zones9.length >= 2
      && /couloirs?[^.;]{0,60}(?:balis|jalonn|mat[ée]rialis|d[ée]limit)|(?:balis[ée]s?|jalonn[ée]s?|mat[ée]rialis[ée]s?)[^.;]{0,40}(?:c[ôo]nes?|plots?|coupelles?)/.test(t9)) {
      const nPlots = (Array.isArray(scene?.markers) ? scene.markers : []).filter((m) => /^(plot|cone|c[ôo]ne|coupelle)$/i.test(m.type || "")).length;
      const need = (zones9.length - 1) * 3;
      if (nPlots < need)
        push("materiel", `couloirs balisés annoncés mais séparations non jalonnées (${nPlots} plot(s) pour ${zones9.length} couloirs — en poser ≥${need}, en ligne le long de chaque séparation)`);
    }
  }

  // 9quinquies) COHÉRENCE DU RALLYE (sports de filet en simple — régression
  //    signalée sur l'exemple VITRINE badminton) : chaque frappe doit partir
  //    de là où le volant/la balle vient de RETOMBER — par le joueur qui s'y
  //    est déplacé. Deux défauts objectifs :
  //    (a) deux frappes de balle CONSÉCUTIVES par le même joueur (l'adversaire
  //        n'a jamais touché : « les volants partent toujours du joueur A ») ;
  //    (b) FRAPPE FANTÔME : l'origine de la frappe n est loin de la retombée
  //        de la frappe n-1 (vitrine : service au fond DROIT, amorti renvoyé
  //        depuis le HAUT du terrain par un joueur resté immobile).
  //    Périmètre : badminton / tennis / tennis de table (au volley, 3 touches
  //    dans le même camp sont la règle). HORS : ateliers multi-volants (≥2
  //    mobiles), terrains dupliqués (_g2…), et textes « pour soi / jonglage /
  //    panier de balles / distributeur » où le rallye n'existe pas.
  if (["badminton", "tennis", "tennis_de_table"].includes(sport)) {
    // DISTRIBUTION : quand un camarade ALIMENTE (casier, envois successifs), il
    // frappe évidemment plusieurs fois de suite — l'alternance n'a pas cours.
    // Vocabulaire aligné sur la dérogation `service_chute` du règlement, qui
    // connaissait déjà « casier » (salve raquettes, R09 : « ton camarade
    // t'envoie successivement un volant au fond droit, un au filet gauche… »).
    // …ET L'ALIMENTATION À SENS UNIQUE, prédicat PARTAGÉ avec le moteur
    // (`isOneWayFeed`, parser.js) : quand un joueur envoie et que l'autre ne
    // fait que se déplacer, enchaîner les frappes EST le dispositif. Sans
    // cette dérogation, le gabarit d'échange corrigé et le juge se
    // contrediraient sur les 10 exercices à sens unique du corpus.
    const waived = isOneWayFeed(t)
      || /pour (?:soi|lui|elle)[- ]?m?[êe]?m?e?|jongl|panier de balles|distributeur|casier|envoie successivement|distribue|multi.?(?:balles?|volants?)|chacun (?:son|sa)|face au mur|contre le mur/i.test(t);
    const multi = players.some((p) => /_g\d/i.test(String(p.id)))
      || markers.filter((m) => ["ballon", "volant", "balle"].includes(String(m.type || "").toLowerCase())).length >= 2;
    // SIMPLE STRICT : exactement un joueur de champ par camp — la logique
    // « chaque frappe répond à la précédente » n'a pas de sens en double ni
    // sur un gabarit mal étiqueté (corpus : un pick-and-roll classé TT).
    const fieldA = players.filter((p) => p.team === "A" && !/observ|arbitre|enseign|coach/i.test(p.role || ""));
    const fieldB = players.filter((p) => p.team === "B" && !/observ|arbitre|enseign|coach/i.test(p.role || ""));
    // Un rallye de simple ne contient NI passe NI dribble : leur présence
    // signale une scène d'un autre sport (corpus : pick-and-roll étiqueté TT)
    // ou un exercice à partenaire — hors du champ de ce contrôle.
    const rallyOnly = !actions.some((a) => a.type === "passe" || a.type === "dribble");
    if (!waived && !multi && rallyOnly && fieldA.length === 1 && fieldB.length === 1) {
      // Positions ÉVOLUÉES : les courses déplacent leur acteur (même convention
      // que la lisibilité) — un joueur qui a couru au filet frappe DU filet.
      const pos = {};
      for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
      const ordered = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
      let prev = null;   // { landing:{x,y}, by }
      for (const a of ordered) {
        if (a.type === "course" || a.type === "dribble") {
          if (a.from && a.to == null && Number.isFinite(+a.tox)) pos[a.from] = { x: +a.tox, y: +a.toy };
          continue;
        }
        if (!["renvoi", "tir"].includes(a.type)) continue;
        const origin = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : pos[a.from];
        const landing = a.to != null ? pos[a.to] : (Number.isFinite(+a.tox) ? { x: +a.tox, y: +a.toy } : null);
        if (prev && a.from != null) {
          // DÉROGATION « SERVICE À DEUX REBONDS » (salve TT nº2, T04) : quand
          // le texte ENSEIGNE le trajet réglementaire du service de tennis de
          // table (« la balle rebondit d'abord sur ma demi-table puis sur la
          // demi-table adverse »), l'IA dessine légitimement le service en
          // DEUX segments enchaînés par le serveur — c'est le trajet demandé,
          // pas un rallye cassé. Mesuré : 0 occurrence sur le lot doré, la
          // dérogation est gratuite.
          const deuxRebonds = sport === "tennis_de_table"
            && /rebondi[trs]*\s+d'abord|deux rebonds|double rebond/i.test(t);
          if (a.from === prev.by && !deuxRebonds)
            push("rallye", `frappes ${prev.order} et ${a.order} enchaînées par ${a.from} : l'adversaire n'a jamais touché le volant/la balle — alterner les frappeurs`);
          // FRAPPE FANTÔME — BADMINTON SEULEMENT : sans rebond, le volant se
          // frappe LÀ où il retombe. Au tennis / tennis de table, la balle
          // REBONDIT puis poursuit vers le frappeur posté derrière — la
          // distance retombée→frappeur y est normale, pas une contradiction.
          else if (sport === "badminton" && origin
            && Math.hypot(origin.x - prev.landing.x, origin.y - prev.landing.y) > 22)
            push("rallye", `frappe fantôme : la frappe ${a.order} (${a.from}) part de (${Math.round(origin.x)},${Math.round(origin.y)}) alors que le volant précédent retombe en (${Math.round(prev.landing.x)},${Math.round(prev.landing.y)}) — déplacer ${a.from} vers la retombée (course) avant sa frappe`);
        }
        if (landing) prev = { landing, by: a.from, order: a.order };
      }
    }
  }

  // 9sexies) DÉFENSE IMMOBILE (👎 prod : « les défenseurs ne suivent pas le
  //    mouvement, on dirait des défenseurs fixes ! ») — le texte donne une
  //    ACTION aux défenseurs (chercher à intercepter, marquer, coulisser,
  //    démarrer, presser…), l'attaque bouge (≥2 actions), et AUCUN défenseur
  //    n'a la moindre flèche. DÉROGATION : défense annoncée passive/statique.
  if (INVASION_SPORTS.has(sport)) {
    // « marqu » exclu : le MARQUAGE est un placement (collé à l'attaquant), pas
    // une flèche — et « démarqué » le contient (2c1 : « sert son partenaire
    // démarqué » déclenchait). Seuls les verbes CINÉTIQUES comptent.
    // 👎 prod 08/08 PROD12 (« les défenseurs sont mal placés […] nul !! ») :
    // « peuvent QUITTER leur joueur pour COUPER une ligne de passe » ne
    // contenait aucun des verbes ci-dessus. Ajout des trois verbes de
    // DÉCROCHAGE — quitter son joueur, couper une ligne, sortir sur le
    // porteur, flotter — tous CINÉTIQUES. « se placent entre leur adversaire
    // et le but », qui figure dans le même texte, reste volontairement DEHORS :
    // c'est un placement, pas une flèche, exactement comme « marqu ».
    const defActive = /d[ée]fenseurs?\b[^.;]{0,90}?(?:cherch|intercept|coulis|press|harc[èe]l|suiv|d[ée]marr|recul|mont|se replac|d[ée]fend|quitten?t?|couper (?:une |la )?ligne|sorten?t? sur|flotte)/i.test(t)
      || /d[ée]fense (?:active|individuelle|homme [àa] homme|tout terrain)/i.test(t);
    const defPassive = /passi[fv]|statique|immobile|sans (?:d[ée]fense|opposition|d[ée]fenseur)|ne bougent? pas|figurant/i.test(t);
    const defs = players.filter((p) => p.team === "B" && !/gardien|observ|arbitre|enseign/i.test(p.role || ""));
    const defIds = new Set(defs.map((p) => p.id));
    const atkMoves = actions.filter((a) => a.order != null && !defIds.has(a.from)).length;
    if (defActive && !defPassive && defs.length >= 1 && atkMoves >= 2
      && !actions.some((a) => defIds.has(a.from)))
      push("defense_immobile", `le texte donne une action aux défenseurs mais aucun n'a de flèche : dessiner au moins leur déplacement principal (coulissage, repli, sortie sur le porteur…)`);
  }

  // 9septies) CIBLES DE PROFONDEUR ALIGNÉES (👎 prod : « servir court, milieu
  //    ou profond, les cerceaux sont sur la même ligne ! ») — le texte demande
  //    de VARIER LA PROFONDEUR, mais les cibles posées sont toutes à la même
  //    distance du filet (elles ne varient qu'en largeur).
  if (NET_SPORTS_F.has(sport)) {
    const depthAsk = /(?:courte?|pr[èe]s du filet)[^.;]{0,60}(?:profonde?|longue?|fond)|(?:profonde?|longue?|fond)[^.;]{0,60}courte?/i.test(t);
    const targets = markers.filter((m) => /^(cerceau|cible|tapis)$/i.test(String(m.type || "")) && Number.isFinite(+m.x));
    if (depthAsk && targets.length >= 2) {
      const xs = targets.map((m) => +m.x);
      if (Math.max(...xs) - Math.min(...xs) < 8)
        push("cibles_profondeur", `des zones courtes ET profondes sont demandées mais les ${targets.length} cibles sont à la même profondeur (x≈${Math.round(xs[0])}) — les étager entre le filet et le fond`);
    }
  }

  // 9septies-bis) CIBLES POSÉES DU MAUVAIS CÔTÉ DU FILET (salve raquettes) —
  //    « trois cerceaux posés dans le demi-terrain ADVERSE » et les trois
  //    cerceaux sortent en x=39, 50 et 62 : le premier dans le camp du
  //    serveur, le deuxième SUR le filet, le troisième dans la « rivière ».
  //    Aucune cible n'était donc atteignable — et `cibles_profondeur`, qui ne
  //    juge que l'ÉTAGEMENT, les trouvait parfaitement échelonnées.
  //    Ne se déclenche que si le texte DIT « adverse / en face » et si le camp
  //    de l'équipe A est franc (sinon c'est un atelier réparti).
  if (NET_SPORTS_F.has(sport) && /adverse|en face|camp oppos[ée]|c[ôo]t[ée] oppos[ée]/i.test(t)) {
    const mates = players.filter((p) => p.team === "A" && Number.isFinite(+p.x));
    const side = mates.length && mates.every((p) => +p.x < 50) ? "left"
      : mates.length && mates.every((p) => +p.x > 50) ? "right" : null;
    const targets = markers.filter((m) => /^(cerceau|cible|tapis)$/i.test(String(m.type || "")) && Number.isFinite(+m.x));
    if (side) for (const m of targets) {
      const wrong = side === "left" ? +m.x < 53 : +m.x > 47;
      if (wrong)
        push("cibles_camp", `la cible ${m.label || m.type} est posée en x=${Math.round(+m.x)}, du mauvais côté du filet — les cibles annoncées « dans le camp adverse » vont ${side === "left" ? "au-delà de x=55 (x 60 à 90)" : "en deçà de x=45 (x 10 à 40)"}`);
    }
  }

  // 9septies-ter) « LONG » ET « COURT » INDISCERNABLES (👎 PROD66 : « aucun
  //    sens, le dégagement long est sur le schéma un coup ÉQUIVALENT à l'amorti
  //    qui suit — même distance ! ») — la scène étiquette elle-même ses coups :
  //    quand elle contient À LA FOIS un coup annoncé LONG (dégagement, lob,
  //    « → fond ») et un coup annoncé COURT (amorti, « → filet »), leurs
  //    retombées doivent VRAIMENT différer en profondeur. Sinon le schéma dit
  //    deux choses et en dessine une seule — c'est le pendant, pour les coups,
  //    de `cibles_profondeur` pour le matériel : une comparaison RELATIVE, sans
  //    seuil absolu à régler.
  //    « LONG DE LIGNE » est écarté : c'est une DIRECTION, pas une profondeur
  //    (le mot « long » y trompe — faux positif repéré en salve).
  //    La dérogation est intrinsèque : sans les deux intentions dans la scène,
  //    la règle ne se déclenche pas.
  if (["badminton", "tennis", "tennis_de_table"].includes(sport)) {
    const LIGNE = /long de ligne|le long de/i;
    const LONG_S = /d[ée]gagement|\blob\b|→ fond|\bfond\b|profonde?|\blongue?s?\b/i;
    const COURT_S = /amorti|→ filet|au filet|\bcourte?s?\b|\bstop\b|contre.?amorti/i;
    const hits = actions.filter((a) => ["renvoi", "tir"].includes(a.type) && Number.isFinite(+a.tox)
      && !LIGNE.test(String(a.label || "")));
    const longs = hits.filter((a) => LONG_S.test(String(a.label || "")));
    const courts = hits.filter((a) => COURT_S.test(String(a.label || "")) && !LONG_S.test(String(a.label || "")));
    if (longs.length && courts.length) {
      const prof = (a) => Math.abs(+a.tox - 50);
      const dLong = Math.max(...longs.map(prof));   // le plus profond des coups longs
      const dCourt = Math.min(...courts.map(prof)); // le plus court des coups courts
      // CALIBRAGE PAR SPORT (salve TT nº2, T03/T11) : la TABLE s'arrête à 28
      // unités du filet — exiger 12 unités d'écart y vaut 43 % de la
      // profondeur totale, et le message « à 32-42 unités du filet » y est
      // PHYSIQUEMENT IMPOSSIBLE (c'est hors table) : la réparation recevait
      // une consigne inapplicable et tournait en rond, et trois tables en
      // bandes parfaitement dessinées partaient au repli. Seuil TT : 8
      // (29 % de la table, le même contraste relatif que 12/44 au badminton).
      // Mesuré : 2 scènes TT du lot doré libérées (PROD39, PROD56), dont les
      // 👎 portaient sur d'autres défauts, attrapés par d'autres règles.
      const seuil = sport === "tennis_de_table" ? 8 : 12;
      const conseil = sport === "tennis_de_table"
        ? "envoyer le coup long près du fond de la TABLE (à 18-26 unités du filet, x ≤ 32 ou x ≥ 68) et le coup court juste derrière le filet (à moins de 10 unités)"
        : "envoyer le coup long près de la ligne de fond adverse (à 32-42 unités du filet) et le coup court juste derrière le filet (à moins de 12)";
      if (dLong - dCourt < seuil)
        push("profondeur_coups", `« ${longs[0].label} » retombe à ${Math.round(dLong)} unités du filet et « ${courts[0].label} » à ${Math.round(dCourt)} : les deux coups sont à la MÊME profondeur, on ne distingue pas le long du court — ${conseil}`);
    }
  }

  // 9octies) REPLACEMENT DEMANDÉ MAIS NON DESSINÉ (👎 prod : « pas de
  //    replacement, que des déplacements, dommage ! ») — le texte exige un
  //    retour au centre après la frappe ; la scène doit porter AU MOINS une
  //    course de replacement (étiquette « replacement / retour / centre » ou
  //    arrivée près du centre de son camp).
  //    ⚠️ L'ÉTIQUETTE NE SUFFIT PLUS (salve raquettes) : « chacun revient au
  //    centre du terrain » donnait deux courses étiquetées « Replacement au
  //    centre » se terminant toutes les deux en x=50 — c'est-à-dire DANS LE
  //    FILET, et au même point pour les deux adversaires. Le centre à
  //    rejoindre est celui de SON camp ; une arrivée hors de son camp n'est
  //    pas un replacement, quoi qu'en dise le libellé (même leçon que les
  //    « courses croisées » PROD22/32 : l'étiquette ment, la géométrie non).
  //    La condition de camp ne s'applique que s'il y a un ADVERSAIRE : dans
  //    un atelier d'étoile à un joueur, le « centre » est celui du dispositif.
  if (NET_SPORTS_F.has(sport)) {
    // « chacun REVIENT au centre » (singulier) manquait au déclencheur, qui ne
    // connaissait que « revenir » et « reviennent » (salve raquettes, R02).
    // « les élèves doivent Y revenir après chaque frappe » (le repère venant
    // d'être nommé — plot, centre du couloir) manquait aussi au déclencheur.
    const ask = /replac|revenir (?:vers|au|à) (?:le |la )?(?:centre|milieu)|retour au (?:centre|milieu)|revien(?:s|t|nent) (?:vers |au |à )?(?:le |la )?(?:centre|milieu)|y revenir|revenir (?:au|sur|à) (?:son |leur |le |la )?(?:plot|rep[èe]re|marque|base)/i.test(t);
    if (ask) {
      const duel = players.some((p) => p.team === "A") && players.some((p) => p.team === "B");
      const byIdR = {}; players.forEach((p) => { byIdR[p.id] = p; });
      const inOwnCamp = (a) => {
        if (!duel) return true;
        const p = byIdR[a.from];
        if (!p || !Number.isFinite(+p.x) || !Number.isFinite(+a.tox)) return true;
        return +p.x < 50 ? +a.tox <= 47 : +a.tox >= 53;
      };
      // …ET une course de LONGUEUR NULLE n'est pas un replacement : deux
      // scènes de la salve « répondaient » à la demande par une flèche de 0
      // unité, invisible au rendu (R21, R23 : « Retour au centre » du joueur
      // vers sa propre position). Le contrôle exige un déplacement RÉEL.
      //    Positions ÉVOLUÉES (même convention que les autres contrôles) : le
      //    joueur qui vient de courir au filet se replace DE LÀ, sa course de
      //    retour est bien réelle même si son point de départ initial est
      //    proche du centre.
      const posR = {}; players.forEach((p) => { posR[p.id] = { x: +p.x, y: +p.y }; });
      const realMove = (a) => {
        const A = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : posR[a.from];
        const long = !A || !Number.isFinite(+a.tox)   // indécidable : on ne bloque pas
          || Math.hypot(+a.tox - A.x, +a.toy - A.y) >= 4;
        if (Number.isFinite(+a.tox) && a.from != null) posR[a.from] = { x: +a.tox, y: +a.toy };
        return long;
      };
      const ordonnees = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order)
        .concat(actions.filter((a) => a.order == null));
      let hasReplacement = false;
      for (const a of ordonnees) {
        if (a.type !== "course" && a.type !== "dribble") continue;
        const bouge = realMove(a);   // TOUJOURS évalué : c'est lui qui fait avancer la position
        // BRANCHE GÉOMÉTRIQUE (pour une course NON étiquetée) : reconnaître la
        // base de replacement du sport. Le badminton n'en a qu'UNE, le centre
        // du demi-court (x≈27/73). Le TENNIS en a DEUX, très éloignées l'une de
        // l'autre (précision de l'enseignant, 08/08) : derrière sa ligne de
        // fond quand on joue du fond (x≈8/92), à ~2 m du filet quand on y monte
        // (x≈43/57). Une bande en x n'y veut donc plus rien dire — au tennis ce
        // qui SIGNE le replacement est le retour sur l'axe CENTRAL de son
        // propre camp, à n'importe quelle profondeur. On ne garde que ça, plus
        // le camp et le déplacement réel. Strictement plus permissif : cette
        // branche ne peut que RECONNAÎTRE des replacements, jamais en refuser.
        const surLAxe = Number.isFinite(+a.toy) && Math.abs(+a.toy - 50) <= 12;
        const surUneBase = sport === "tennis"
          ? true                                   // les deux bases sont sur l'axe : la profondeur ne tranche pas
          : Number.isFinite(+a.tox) && (Math.abs(+a.tox - 27) <= 12 || Math.abs(+a.tox - 73) <= 12);
        const bon = bouge && inOwnCamp(a)
          && (/replac|retour|centre|milieu/i.test(String(a.label || ""))
            || (surLAxe && surUneBase && Number.isFinite(+a.tox)));
        if (bon && a.type === "course") hasReplacement = true;
      }
      if (!hasReplacement)
        // La BASE dépend du sport ET de la zone de jeu (👎 prod 08/08 PROD102,
        // tennis : « centre mais DERRIÈRE la ligne de fond, pas au milieu du
        // terrain !! », précisé par l'enseignant : au tennis il y a DEUX bases,
        // fond de court derrière sa ligne et filet à ~2 m ; au badminton une
        // seule, le centre). Le message est lu par la RÉPARATION : lui dicter
        // x≈27/73 au tennis, c'est lui faire redessiner le défaut signalé. Le
        // VERDICT ne change pas — seule la consigne de correction s'adapte.
        push("replacement", `un replacement au centre est demandé après la frappe, mais aucune course de replacement n'est dessinée — ajouter le retour au centre de SON camp (${sport === "tennis" ? "derrière sa ligne de fond s'il joue du fond, x≈8 à gauche / x≈92 à droite ; à ~2 m du filet s'il y monte, x≈43 / x≈57" : "x≈27 à gauche, x≈73 à droite"}), jamais sur le filet`);
    }
  }

  // 9octies-bis) DÉPLACEMENT DEMANDÉ, SCÈNE FIGÉE (backlog PROD51 « déplacements
  //    latéraux TT », confirmé par la salve raquettes : « travail des
  //    déplacements latéraux, le relanceur joue alternativement coup droit
  //    puis revers, il se déplace le long de sa demi-table » → quatre frappes,
  //    ZÉRO flèche de course). C'est le pendant, aux sports de filet, de
  //    `defense_immobile` : le texte fait du DÉPLACEMENT l'objet même de la
  //    situation, la scène n'en dessine aucun. Volontairement binaire (aucune
  //    course NI rotation dans toute la scène) : on ne juge pas la qualité du
  //    déplacement, seulement son absence totale.
  //    DÉROGATIONS : travail annoncé sur place / sans déplacement.
  if (NET_SPORTS_F.has(sport)) {
    // 👎 prod 08/08 PROD6 (« les joueurs doivent quitter la zone centrale, pour
    // laisser la place au suivant ! il n'y a aucun mouvement sur le schéma ») :
    // la ROTATION scolaire du volley — quitter sa zone après sa touche pour
    // rendre la main au partenaire — est un déplacement DEMANDÉ que le
    // vocabulaire ci-dessus ne nommait pas (le texte ne dit jamais
    // « déplacement »). C'est le même défaut que PROD51, dit avec d'autres mots.
    const askMove = /d[ée]placements?\b|se d[ée]place|se d[ée]placer|d[ée]placer.se|va chercher (?:la balle|le volant)|monte au filet|montée au filet|quitten?t? (?:la |leur )?(?:zone|place)|laisser (?:la place|un partenaire|sa place)|c[èe]den?t? (?:la|sa) place|sorten?t? de la zone/i.test(t);
    const still = /sur place|sans (?:se )?d[ée]plac|sans bouger|statique|immobile|pieds fixes/i.test(t);
    if (askMove && !still && !actions.some((a) => a.type === "course" || a.type === "rotation"))
      push("deplacement", "le texte fait du DÉPLACEMENT l'objet de la situation, mais la scène ne contient aucune flèche de course — dessiner les déplacements décrits (aller chercher la balle, revenir, longer la table…) comme des actions « course » ordonnées");
  }

  // 9nonies) COURSES CROISÉES DEMANDÉES (👎 prod PROD22 « il n'y a pas de
  //    courses croisées », PROD32 « le joueur 1 ne va pas vers le centre ») —
  //    le texte demande un CROISEMENT ; deux courses de COÉQUIPIERS doivent
  //    réellement se couper (positions évoluées, intersection géométrique de
  //    segments). Même équipe exigée : un attaquant qui coupe la route d'un
  //    défenseur n'est pas « des courses croisées ».
  //    ⚠️ Le déclencheur, l'extraction des courses et le test d'intersection
  //    sont AU NIVEAU MODULE (`ASK_CROSS`, `crossRuns`, `crossedPair`) : le
  //    correcteur `enforceCrossedRuns` les réutilise LITTÉRALEMENT. Les
  //    recopier serait le désalignement que ce dépôt a déjà payé huit fois.
  if (ASK_CROSS.test(t) && !crossedPair(crossRuns(scene)))
    push("croisement", CROISEMENT_MSG);

  // 9decies) ACTION QUI NE VA NULLE PART (salve raquettes) — une action dont
  //    l'arrivée est SON PROPRE AUTEUR (`from === to`) : le rendu n'en dessine
  //    qu'un moignon collé au pion, et le geste décrit disparaît. Vu sur une
  //    fiche « varier la longueur du service » : trois services annoncés
  //    court / mi-court / long, tous écrits { from:"A1", to:"A1" } — la scène
  //    ne montrait AUCUN service, et aucun contrôle ne bronchait (la chute du
  //    service n'a pas de coordonnées à examiner).
  //    NB : ce n'est PAS la convention « touches pour soi » du volley, qui est
  //    une petite flèche vers un POINT (tox/toy) au-dessus du joueur.
  //    Toutes familles, aucune dérogation : viser son propre pion n'a jamais
  //    de sens. Zéro déclenchement sur tout le lot doré (729 scènes routées).
  for (const a of actions) {
    if (a.from == null || a.from !== a.to) continue;
    push("action_vide", `l'action ${a.order ?? ""} (${a.type}${a.label ? ` « ${a.label} »` : ""}) part de ${a.from} et revient sur ${a.from} : elle ne va nulle part — viser un POINT ("tox"/"toy") ou un AUTRE joueur`);
  }
  //    MÊME DÉFAUT, EXPRIMÉ EN COORDONNÉES : une course dont l'arrivée est son
  //    propre point de départ. Elle ne dessine rien… sauf son numéro d'étape :
  //    l'enseignant voit une pastille « 3 » qui promet un déplacement
  //    inexistant (salve raquettes, R21/R23 : deux « Retour au centre » de
  //    longueur 0 collés au joueur, alors que le texte demandait justement de
  //    voir le replacement). Positions ÉVOLUÉES. Seuil très serré (0,5) : un
  //    petit ajustement d'appui reste légitime.
  //    Le MOTEUR n'en produit plus (dropStillMoves, analysis.js) : la règle ne
  //    coûte rien au lot doré et ne parle qu'aux scènes de l'IA.
  {
    const posV = {};
    for (const p of players) posV[p.id] = { x: +p.x, y: +p.y };
    const ordonneesV = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order)
      .concat(actions.filter((a) => a.order == null));
    for (const a of ordonneesV) {
      if (a.type !== "course" && a.type !== "dribble") continue;
      const A = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : posV[a.from];
      const B = a.to != null ? posV[a.to] : (Number.isFinite(+a.tox) ? { x: +a.tox, y: +a.toy } : null);
      if (!A || !B || !Number.isFinite(A.x) || !Number.isFinite(B.x)) continue;
      if (Math.hypot(B.x - A.x, B.y - A.y) < 0.5) {
        // REPLACEMENT NUL (salve raquettes nº3, Q02/Q06/Q07/Q18 — 4 rejets,
        // 0 réparation, et la réparation de Q02 en produisait QUATRE au lieu
        // d'un) : le conseil générique « donner une vraie arrivée » prescrit
        // la MAUVAISE extrémité — l'arrivée d'un replacement (le centre) est
        // déjà bonne, c'est le DÉPART qui est faux : le joueur n'a jamais été
        // excentré par sa frappe. Le message dit la séquence complète.
        if (/replac|retour au (?:centre|plot)|revient/i.test(a.label || ""))
          push("action_vide", `le replacement ${a.order ?? ""} de ${a.from} arrive là où il part (${Math.round(A.x)},${Math.round(A.y)}) : son ARRIVÉE (la position de base) est bonne, c'est son DÉPART qui est faux — le joueur doit d'abord être EXCENTRÉ par sa frappe : donner à la frappe précédente une origine excentrée (fromx/fromy) ou une course vers le volant/la balle AVANT elle, puis faire PARTIR ce replacement de ce point excentré`);
        else
          push("action_vide", `le déplacement ${a.order ?? ""}${a.label ? ` « ${a.label} »` : ""} de ${a.from} arrive là où il part (${Math.round(A.x)},${Math.round(A.y)}) : la flèche est invisible et seul son numéro s'affiche — lui donner une VRAIE arrivée, ou la supprimer`);
      } else if (a.to == null && a.from != null) posV[a.from] = { x: B.x, y: B.y };
    }
  }

  // 10) RÔLE ANNONCÉ MAIS ABSENT (retours prod 👎 PROD1/3/4/7/9) — le texte
  //     peuple la situation de personnes qui ne sont ni attaquants ni
  //     défenseurs : l'ENSEIGNANT qui distribue ou se poste, l'OBSERVATEUR /
  //     arbitre / juge. Elles font partie du dispositif que l'enseignant veut
  //     projeter ; les omettre, c'est dessiner une autre situation.
  {
    const AP = "['’]";   // les textes réels mêlent apostrophe droite et courbe

    // 10a) ENSEIGNANT — seulement s'il TIENT UN POSTE ou ALIMENTE le jeu.
    //      DÉROGATION : un enseignant qui « circule », « questionne »,
    //      « corrige », « observe » est une consigne PÉDAGOGIQUE, pas un
    //      élément de la scène — il ne doit surtout pas être dessiné (PROD10 :
    //      « L'enseignant circule entre les tables et questionne »).
    const TEACHER = `(?:l${AP}\\s*)?(?:enseignant|professeur|prof|[ée]ducateur|entra[iî]neur|coach)e?s?`;
    const POSTED = "lance|envoie|distribue|sert\\b|alimente|propulse|jette|met en jeu|renvoie|frappe|se tient|se place|se positionne|se situe|est plac|se met|tient|occupe|depuis";
    const teacherPosted = new RegExp(`\\b${TEACHER}\\b[^.;:]{0,60}?\\b(?:${POSTED})`, "i").test(t)
      || new RegExp(`\\b(?:${POSTED})[^.;:]{0,40}?\\bpar\\s+${TEACHER}\\b`, "i").test(t);
    const hasTeacher = players.some((p) => /enseign|professeur|^prof|[ée]ducateur|entra[iî]neur|coach|distributeur/i.test(String(p.role || ""))
      || String(p.id || "").toUpperCase() === "E");
    if (teacherPosted && !hasTeacher)
      push("role_absent", "l'enseignant tient un poste dans la situation (il distribue ou se place) mais aucun joueur « Enseignant » n'est dessiné");

    // 10b) OBSERVATEUR / ARBITRE / JUGE nommé dans le texte.
    //      DÉROGATION : « sans observateur / sans arbitre » éteint le contrôle.
    const obsNamed = new RegExp(`\\b(?:observateurs?|arbitres?|arbitrent|juges?|chronom[èe]tre(?:ur)?s?|marqueurs?)\\b`, "i").test(t)
      || new RegExp(`\\bcamarades?\\b[^.;:]{0,40}?\\b(?:annonce|observe|compte|note|v[ée]rifie|contr[ôo]le)`, "i").test(t);
    const obsWaived = /\bsans\s+(?:observateur|arbitre|juge)/i.test(t);
    // Côté scène, le rôle porte des NOMS VARIÉS selon le gabarit qui l'a posé :
    // même vocabulaire que `observerFromText` (la source qui les crée), plus
    // « starter » / « officiel » (course de vitesse) et « compteur ».
    const hasObserver = players.some((p) => /observ|arbitre|juge|chronom|starter|officiel|camarade|marqueur|compteur|secr[ée]taire/i.test(String(p.role || ""))
      || String(p.team || "").toUpperCase() === "OBS");
    if (obsNamed && !obsWaived && !hasObserver)
      push("role_absent", "un observateur (ou arbitre/juge) est décrit dans le dispositif mais n'est dessiné nulle part");
  }

  // 9) CONTRÔLES GÉOMÉTRIQUES OBJECTIFS existants (tir depuis son camp, passe
  //    fantôme, écran annoncé non dessiné) — partagés avec le pipeline.
  for (const q of qualityIssues(scene, sport, text)) push("qualite", q);

  return issues;
}

/* ============================================================================
   ÉTAGER LE LONG ET LE COURT (correcteur de `profondeur_coups`)
   ----------------------------------------------------------------------------
   Première cause de rejet restante de la famille raquettes (5 cas sur 22), et
   la seule qui n'ait PAS bougé quand les correcteurs de position ont été
   branchés avant la garde : le modèle pose « Dégagement » et « Service court »
   à la même distance du filet, malgré un message de réparation qui donne les
   bandes au chiffre près. Il les lit et ne les applique pas — quatre rejeux le
   montrent.

   Or ce message DIT la correction : « le coup long à 32-42 unités du filet, le
   coup court à moins de 12 » (18-26 / moins de 10 à la table). Il n'y avait
   personne pour l'appliquer. Cette fonction le fait, avec exactement les mêmes
   constantes et le même prédicat que la règle — elle ne s'exécute donc QUE sur
   les scènes que la règle rejetterait, et jamais sur une scène déjà étagée.

   Le CAMP de retombée est conservé : on ne déplace le coup que le long de
   l'axe de profondeur, jamais d'un côté du filet à l'autre (c'est le travail
   de `enforceRegulation`, § 3). La hauteur (y) n'est pas touchée : elle porte
   la direction — croisé, décroisé, long de ligne. */
export function enforceShotDepth(scene, sport, text = "") {
  if (!["badminton", "tennis", "tennis_de_table"].includes(sport)) return scene;
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const LIGNE = /long de ligne|le long de/i;
  const LONG_S = /d[ée]gagement|\blob\b|→ fond|\bfond\b|profonde?|\blongue?s?\b/i;
  const COURT_S = /amorti|→ filet|au filet|\bcourte?s?\b|\bstop\b|contre.?amorti/i;
  const hits = actions.filter((a) => ["renvoi", "tir"].includes(a.type) && Number.isFinite(+a.tox)
    && a.to == null && !LIGNE.test(String(a.label || "")));
  const longs = hits.filter((a) => LONG_S.test(String(a.label || "")));
  const courts = hits.filter((a) => COURT_S.test(String(a.label || "")) && !LONG_S.test(String(a.label || "")));
  if (!longs.length || !courts.length) return scene;
  const prof = (a) => Math.abs(+a.tox - 50);
  const seuil = sport === "tennis_de_table" ? 8 : 12;
  if (Math.max(...longs.map(prof)) - Math.min(...courts.map(prof)) >= seuil) return scene;   // déjà étagé
  // Milieu des bandes que le message prescrit — pas un bord, pour laisser de la
  // marge au seuil et ne pas coller la retombée à la ligne de fond.
  const D_LONG = sport === "tennis_de_table" ? 22 : 37;
  const D_COURT = sport === "tennis_de_table" ? 7 : 9;
  /* LE SERVICE A SES PROPRES BORNES, et elles priment sur l'étagement. Trou
     trouvé par l'A/B de la salve raquettes : un « service court » posé à 9
     unités du filet retombe à x=59 — c'est-à-dire DANS LA RIVIÈRE du badminton
     (le service doit dépasser la ligne de service courte, |x−50| ≥ 16). Le
     correcteur créait donc un `service_chute` en réparant un
     `profondeur_coups`. Au tennis, la contrainte est inverse : le service doit
     rester DANS le carré, donc ne pas aller au fond. On borne des deux côtés,
     avec les constantes de `NET_COURTS` (badminton 34, tennis 28). */
  const estService = (a) => /service|engagement|mise en jeu/i.test(String(a.label || ""));
  const borneService = (d, a) => {
    if (!estService(a)) return d;
    if (sport === "badminton") return Math.max(d, 18);   // au-delà de la ligne de service courte
    if (sport === "tennis") return Math.min(d, 20);      // dans le carré de service
    return d;
  };
  const poser = (a, d) => { const dd = borneService(d, a); a.tox = Math.round(+a.tox >= 50 ? 50 + dd : 50 - dd); };
  for (const a of longs) poser(a, D_LONG);
  for (const a of courts) poser(a, D_COURT);
  return scene;
}

/* ============================================================================
   RECULER LE RECEVEUR (correcteur de `passe_avant`)
   ----------------------------------------------------------------------------
   `passe_avant` est la doyenne des classes de rejet du dépôt, et depuis le
   rejeu de clôture sa PREMIÈRE cause (14 rejets). Elle a toujours été traitée
   par le prompt et par le message de réparation ; la salve rugby nº2 avait
   déjà constaté « 3 rejets, 0 réparation aboutie — l'IA redessine le défaut ».

   Le message dit pourtant la correction mot pour mot : « RECULER le point de
   réception : au moment de la passe, le receveur doit être derrière le
   passeur ». Personne ne l'appliquait. Cette fonction le fait, avec le MÊME
   prédicat, le MÊME suivi de positions et la MÊME tolérance que la règle —
   elle ne peut donc pas s'exécuter sur une passe que la règle accepte.

   Ce qu'elle préserve : la HAUTEUR du receveur (y), qui porte tout le sens
   tactique d'un jeu déployé — « écarte », « passe au large », « saute-passe ».
   On ne recule que sur l'axe de progression, de 2 unités derrière le passeur.
   Et si le receveur est arrivé là par une COURSE, c'est l'arrivée de la course
   qu'on recule, pas la position de départ du joueur : sinon la flèche et la
   pastille se contrediraient.

   Le jeu au pied est exclu comme dans la règle : il a le droit d'aller devant. */
export function enforceBackwardPass(scene, sport, text = "") {
  if (sport !== "rugby") return scene;
  const players = (Array.isArray(scene?.players) ? scene.players : [])
    .filter((p) => Number.isFinite(+p.x) && Number.isFinite(+p.y));
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  if (!players.length || !actions.length) return scene;
  const zones = Array.isArray(scene?.zones) ? scene.zones : [];
  // Même garde que la règle : un passe-et-suit en couloirs fait circuler la
  // balle vers l'avant, la règle de jeu ne s'y applique pas.
  const isDrill = actions.some((a) => a.type === "rotation")
    || actions.filter((a) => a.type === "tir").length >= 2
    || zones.some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""));
  if (isDrill) return scene;
  const PIED = /\bpied\b|chandelle|\bdrop\b|p[ée]nalit|transformation|d[ée]gagement|grubber|up.?and.?under|coup de pied|\bbott/i;

  const byId = {}; players.forEach((p) => { byId[p.id] = p; });
  const pos = {}; players.forEach((p) => { pos[p.id] = { x: +p.x, y: +p.y } });
  const venuDe = {};                       // dernière action ayant posé la position
  const acts = actions.slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  for (const a of acts) {
    if (a.type === "passe" && a.from && a.to && pos[a.from] && pos[a.to]
      && !PIED.test(String(a.label || "")) && pos[a.to].x > pos[a.from].x + 3) {
      const cible = Math.max(3, Math.min(97, Math.round(pos[a.from].x - 2)));
      const source = venuDe[a.to];
      if (source) source.tox = cible;      // le receveur est arrivé en courant : on recule son ARRIVÉE
      else if (byId[a.to]) byId[a.to].x = cible;   // sinon sa position de départ
      pos[a.to] = { x: cible, y: pos[a.to].y };
      if (a.tox != null) a.tox = cible;    // et la pointe de la passe suit
    }
    if ((a.type === "course" || a.type === "dribble") && a.from && a.tox != null && pos[a.from]) {
      pos[a.from] = { x: +a.tox, y: +a.toy }; venuDe[a.from] = a;
    }
    if (a.type === "passe" && a.to && a.tox != null) { pos[a.to] = { x: +a.tox, y: +a.toy }; venuDe[a.to] = a; }
  }
  return scene;
}

/* ============================================================================
   L'EFFECTIF QUE LE TEXTE ANNONCE (lecture partagée juge ↔ moteur)
   ----------------------------------------------------------------------------
   `effectif` est la PREMIÈRE cause de défaut de la route template/local du
   repli : 98 signalements sur 1155 scènes, répartis sur DIX gabarits — chacun
   dessine un squelette figé (« Porteur, Ailier droit, Défenseur ») quel que
   soit le nombre annoncé. « Trois attaquants face à deux défenseurs » sort à
   deux pions.

   Corriger gabarit par gabarit serait dix chantiers. Les JUGES, eux, savent
   déjà compter — cette fonction expose leur lecture pour qu'un correcteur
   unique l'applique (`completeRoster`, analysis.js). Elle est
   VOLONTAIREMENT CONSERVATRICE : elle ne rend un nombre que sur les tournures
   sans ambiguïté, et 0 signifie « je ne sais pas », jamais « zéro joueur ».

   Renvoie { a, b } — minimum de joueurs de CHAMP attendus de chaque côté. */
export function expectedRoster(text, sport) {
  const t = coreSituationText(text);
  const FILET = new Set(["volleyball", "badminton", "tennis", "tennis_de_table"]);
  const invasion = INVASION_SPORTS.has(sport), filet = FILET.has(sport);
  if (!invasion && !filet) return { a: 0, b: 0 };
  let a = 0, b = 0;
  const borne = (n) => (n >= 1 && n <= 11 ? n : 0);
  // « N contre M » — le signe × et le x valent « contre » hors dimensions,
  // exactement comme dans les deux règles d'effectif.
  const tD = duelText(t);
  const duel = tD.match(new RegExp(`(?<![-\\d])\\b(${NUMRE})\\s*contre\\s*(${NUMRE})\\b(?![-\\d])(?!\\s+(?:bloc|d[ée]fens|zone|rideau|ligne|syst[èe]me))`, "i"));
  if (duel) { a = borne(num(duel[1])); b = borne(num(duel[2])); }
  // Comptes NOMMÉS d'un seul côté — ils PRIMENT sur le duel quand ils sont
  // plus exigeants (« 3 contre 3 » puis « quatre attaquants » : on prend 4).
  // INVASION SEULEMENT, comme les règles `mb`/`mr` qui vivent dans le bloc
  // `INVASION_SPORTS` : au filet il n'y a pas d'attaquants et de défenseurs,
  // il y a DEUX CAMPS — « deux défenseurs » n'y désigne pas l'équipe B, et
  // l'appliquer ajoutait un renfort à côté de l'enseignant-distributeur.
  if (invasion) {
    const mb = t.match(new RegExp(`(${NUMRE})\\s+(?:joueurs?\\s+)?(?:bleus?|attaquants?)\\b`, "i"));
    if (mb) a = Math.max(a, borne(num(mb[1])));
    const mr = t.match(new RegExp(`(${NUMRE})\\s+(?:joueurs?\\s+)?(?:rouges?|d[ée]fenseurs?)\\b`, "i"));
    if (mr) b = Math.max(b, borne(num(mr[1])));
  }
  // SYSTÈME DÉFENSIF NOMMÉ — « zone 2-3 », « défense 6-0 », « bloc en 4-4-2 » :
  // la somme des rangées chiffre l'effectif DÉFENSIF. Même prédicat que la
  // règle `1quater`, et invasion seulement comme elle. Deuxième trou du
  // correcteur d'effectif : 11 des 37 signalements restants.
  if (invasion) {
    const sm = t.match(/(?:zone|d[ée]fense|bloc)\s+(?:bas\s+|m[ée]dian\s+|haut\s+)?(?:replac[ée]e?\s+)?(?:en\s+)?(\d(?:-\d){1,3})\b/i);
    if (sm) {
      const sum = sm[1].split("-").reduce((x, dd) => x + +dd, 0);
      if (sum >= 3 && sum <= 11) b = Math.max(b, sum);
    }
  }
  /* ⚠️ AU FILET, LE COMPTE NOMMÉ N'EST PAS LU — ET C'EST DÉLIBÉRÉ, APRÈS MESURE.
     « trois réceptionneurs », « 4 élèves » : ma règle « dessiner TOUS LES N »
     les signale (6 cas), mais rien ici ne les rend — donc rien ne les répare.
     J'ai essayé de les attribuer au camp qui travaille (celui qui compte le
     plus de joueurs, A à égalité) : MESURÉ NÉGATIF — route template 1044 ->
     1042, route analysis 626 -> 621. Deviner le côté crée plus de défauts
     qu'il n'en répare, parce qu'au filet « trois réceptionneurs » peut désigner
     l'un OU l'autre camp, et rien dans la scène ne tranche.
     Le trou reste donc OUVERT et documenté : il demande de savoir QUEL camp
     travaille, ce qui se lit dans le texte (« en face », « adverse »), pas dans
     la géométrie. */
  // « (deux) équipes de N » : les DEUX camps portent N.
  const em = t.match(new RegExp(`\\b(?:deux|2)\\s+[ée]quipes\\s+de\\s+(${NUMRE})\\b`, "i"));
  if (em) { const n = borne(num(em[1])); if (n >= 2) { a = Math.max(a, n); b = Math.max(b, n); } }
  // « EN DOUBLE » : deux joueurs de champ par camp, et cela ne se dit pas en
  // chiffres. Les tournures de SCORE sont retirées avant le test, comme dans
  // la règle (« un point compte double » n'est pas un format).
  if (filet) {
    const sansScore = t.replace(/(?:compte(?:nt)?|vaut|valent|rapporte(?:nt)?|marque(?:nt)?)\s+(?:un\s+point\s+)?doubles?|points?\s+doubles?|score\s+double|double\s+point/gi, " ");
    if (/\bdoubles?\b/i.test(sansScore) && !/double.?pas|double.?p[ée]nalty|doubler|double.?contact/i.test(t)) { a = Math.max(a, 2); b = Math.max(b, 2); }
  }
  return { a, b };
}

/* ============================================================================
   CONDUIRE LE TIR JUSQU'À LA CIBLE (correcteur des ARRIVÉES de tir)
   ----------------------------------------------------------------------------
   Au rejeu complet du 12/08, le TIR est devenu la PREMIÈRE cause de rejet du
   dépôt — 8 rejets une fois regroupées les quatre classes que `classOf` éclate
   (tir interrompu, tir qui ne va pas au panier, tir hors de portée, tir depuis
   son propre camp). Aucune n'a jamais eu de correcteur.

   Elles se partagent en DEUX natures, et cette fonction ne traite que la
   première :
     • l'ARRIVÉE est fausse (le tir s'arrête avant la cage, sort du cadre, rate
       le cercle) — il suffit de conduire la pointe de la flèche jusqu'à la
       cible. Personne n'est déplacé : c'est pur, et c'est ici ;
     • l'ORIGINE est fausse (tir hors de portée, tir depuis son propre camp) —
       il faut insérer une course d'élan, donc déplacer le tireur. Autre risque,
       autre chantier ; volontairement HORS de cette fonction.

   Comme `enforceShotDepth` et `enforceBackwardPass`, elle applique ce que le
   message de chaque règle prescrit déjà mot pour mot (« PROLONGER la flèche de
   tir jusqu'à la cage », « faire ARRIVER la flèche SUR le panier »), avec LE
   MÊME prédicat, LES MÊMES constantes et LES MÊMES dérogations — elle ne peut
   donc pas s'exécuter sur un tir que la règle accepte.

   La HAUTEUR voulue par le modèle est préservée quand elle est jouable : un
   tir annoncé au ras du poteau reste excentré, ramené dans le cadre (42-58) et
   non recentré d'autorité. */
export function enforceShotTarget(scene, sport, text = "") {
  const REACH = { handball: 34, futsal: 34, basketball: 34, basket3x3: 34, football: 48, rugby: 48 };
  const CAGE = new Set(["handball", "futsal", "football"]);
  const reach = REACH[sport];
  if (!reach) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const acts = (Array.isArray(scene?.actions) ? scene.actions : [])
    .filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  if (!acts.length) return scene;
  // Les dérogations de la règle, recopiées telles quelles : buts MATÉRIALISÉS
  // (la géométrie de la cage officielle ne s'applique plus) et tir DÉCRIT
  // manqué/contré (son arrivée hors cadre est le sujet du texte).
  const full = String(text || "");
  const goalsMat = /mini[- ]buts?|multibuts?|buts?[^.;]{0,40}(?:plots?|cônes?|coupelles?|matérialis)|(?:plots?|cônes?|coupelles?)[^.;]{0,40}buts?/i.test(full);
  const tirWaiver = goalsMat || /non cadr|tirs?[^.;]{0,25}(?:manqu[ée]|rat[ée]|contr[ée]|à côté)|à côté du but|au[- ]dessus du but|arrêt du gardien|parade|air ?ball/i.test(full);
  const nearTarget = (x, y) => (Array.isArray(scene?.markers) ? scene.markers : [])
    .some((m) => ["but", "cible", "plot", "cone", "cône", "coupelle", "cerceau"].includes(String(m.type || "").toLowerCase())
      && Math.hypot(+m.x - x, +m.y - y) < 10)
    || (Array.isArray(scene?.zones) ? scene.zones : []).some((z) => /cible|viser|atteindre/i.test(String(z.label || ""))
      && x >= +z.x && x <= +z.x + (+z.w || 0) && y >= +z.y && y <= +z.y + (+z.h || 0));
  const pos = {}; for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
  // Le cadre utile : la cage occupe y 40 à 60, on vise 42-58 pour ne pas coller
  // la flèche au poteau (même marge que les bandes de `enforceShotDepth`).
  const dansLeCadre = (y) => Math.max(42, Math.min(58, Number.isFinite(+y) ? +y : 50));
  for (const a of acts) {
    const from = a.fromx != null ? { x: +a.fromx, y: +a.fromy } : pos[a.from];
    const to = a.tox != null ? { x: +a.tox, y: +a.toy } : null;
    if (a.type === "tir" && from && to) {
      if (to.x >= 88 || to.x <= 12) {
        const goalX = to.x >= 88 ? 100 : 0;
        // La branche « hors de portée » appartient à l'autre chantier : quand
        // elle s'applique, la règle n'évalue MÊME PAS le cadre (`else if`) —
        // on respecte cette structure, sinon on corrigerait un tir dont le
        // défaut signalé est ailleurs.
        if (Math.abs(from.x - goalX) > reach && !goalsMat) { /* origine — hors périmètre */ }
        else if (CAGE.has(sport) && (to.y < 37 || to.y > 63) && !tirWaiver && !nearTarget(to.x, to.y))
          a.toy = dansLeCadre(to.y);
      } else if (CAGE.has(sport) && !tirWaiver && !nearTarget(to.x, to.y)
        && ((to.x > from.x && to.x > 60) || (to.x < from.x && to.x < 40))) {
        // TIR INTERROMPU : « PROLONGER la flèche jusqu'à la cage ». On prolonge
        // dans la DIRECTION que le modèle a choisie, sans la redresser : la
        // hauteur d'arrivée est recalculée sur la droite du tir, puis ramenée
        // dans le cadre. Un tir croisé reste croisé.
        const cible = to.x > from.x ? 98 : 2;
        const k = (cible - from.x) / (to.x - from.x || 1);
        a.toy = dansLeCadre(from.y + (to.y - from.y) * k);
        a.tox = cible;
      }
      if ((sport === "basketball" || sport === "basket3x3") && !tirWaiver && !nearTarget(+a.tox, +a.toy)) {
        const hoop = (sport === "basket3x3" || +a.tox > 50) ? { x: 98.5, y: 50 } : { x: 1.5, y: 50 };
        if (Math.hypot(+a.tox - hoop.x, +a.toy - hoop.y) > 10) { a.tox = hoop.x; a.toy = hoop.y; }
      }
    }
    if ((a.type === "course" || a.type === "dribble") && a.from && a.tox != null && a.to == null)
      pos[a.from] = { x: +a.tox, y: +a.toy };
  }
  return scene;
}

/* ============================================================================
   AMENER LE TIREUR À PORTÉE (correcteur des ORIGINES de tir)
   ----------------------------------------------------------------------------
   La seconde moitié du chantier TIR, laissée ouverte quand `enforceShotTarget`
   a traité les ARRIVÉES : les défauts d'ORIGINE demandent de déplacer le
   tireur, pas la pointe de la flèche. Devenus la PREMIÈRE cause de la route
   template du repli — `tir hors de portée` 11 et `tir depuis son propre camp`
   10, souvent sur la MÊME scène (un tir parti de x=40 est à la fois dans son
   camp et à 60 unités du panier).

   Les deux messages disent la correction, et c'est le même geste :
   « faire PRÉCÉDER le tir d'une course d'élan qui amène le tireur à x≥66, et
   faire partir le tir de l'ARRIVÉE de cette course ». Cette fonction l'écrit :
   une course d'élan s'intercale, le tireur la suit, et l'origine du tir la
   rejoint.

   Prédicats et constantes recopiés des deux règles (REACH par sport, seuil 50
   pour le camp, dérogations `goalsMat` et « thème du tir de loin »). La
   HAUTEUR est conservée : elle porte le couloir d'attaque (aile, axe). */
export function enforceShotOrigin(scene, sport, text = "") {
  const REACH = { handball: 34, futsal: 34, basketball: 34, basket3x3: 34, football: 48, rugby: 48 };
  const reach = REACH[sport];
  if (!reach) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const zones = Array.isArray(scene?.zones) ? scene.zones : [];
  if (!players.length || !actions.length) return scene;
  const full = String(text || "");
  // Dérogations, recopiées : buts MATÉRIALISÉS (la géométrie officielle ne
  // s'applique plus) et THÈME du tir lointain (« marquer depuis son camp
  // compte double ») — là, la frappe de loin EST l'objet de la situation.
  if (/mini[- ]buts?|multibuts?|buts?[^.;]{0,40}(?:plots?|cônes?|coupelles?|matérialis)|(?:plots?|cônes?|coupelles?)[^.;]{0,40}buts?/i.test(full)) return scene;
  if (/(?:but|marqu\w*|frapp\w*|tir\w*|lob\w*)[^.;]{0,30}(?:depuis|dès|de) (?:son|sa|leur) (?:propre )?(?:camp|moiti[ée])|(?:frappe|tir)s?[^.;]{0,20}(?:de loin|longue distance|du milieu de terrain)/i.test(full)) return scene;
  // Même signal d'ATELIER que `qualityIssues` : dans un dispositif à cellules,
  // « tirer depuis son camp » n'a pas de sens — chaque poste est local.
  if (actions.some((a) => a.type === "rotation")
    || actions.filter((a) => a.type === "tir").length >= 2
    || zones.some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""))) return scene;

  const pos = {}; for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
  const ordered = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  const ajouts = [];
  // Le dernier DÉPLACEMENT connu, pour le retrouver depuis l'origine du tir.
  let dernierMouv = null;
  for (const a of ordered) {
    if ((a.type === "course" || a.type === "dribble" || a.type === "ecran") && a.tox != null && a.to == null) {
      if (a.from) pos[a.from] = { x: +a.tox, y: +a.toy };
      dernierMouv = a; continue;
    }
    /* ⚠️ LE TIR N'A PAS TOUJOURS DE PROPRIÉTAIRE. Le moteur pose volontiers une
       flèche à origine EXPLICITE et sans `from` : « 2 tir undefined fromx=40 ».
       La première version l'écartait (`if (!a.from) continue`) et ne réparait
       donc RIEN sur le corpus — 22 signalements intacts, correcteur muet.
       Or l'information est là : le DÉPLACEMENT précédent finit exactement à
       cette origine. C'est lui la « course d'élan » que le message réclame — il
       suffit de la PROLONGER, ce qui vaut mieux qu'ajouter une flèche de plus. */
    if (a.type !== "tir") continue;
    const from = a.fromx != null ? { x: +a.fromx, y: +a.fromy } : (a.from ? pos[a.from] : null);
    const to = a.tox != null ? { x: +a.tox, y: +a.toy } : null;
    if (!from || !to) continue;
    // Le but visé : celui vers lequel la flèche part. Sans arrivée franche, on
    // ne devine pas — la règle non plus.
    const goalX = to.x >= 88 ? 100 : to.x <= 12 ? 0 : (to.x > from.x ? 100 : 0);
    const horsPortee = Math.abs(from.x - goalX) > reach;
    const proprecamp = GOAL_SPORTS.has(sport) && (goalX === 100 ? from.x < 50 : from.x > 50);
    if (!horsPortee && !proprecamp) continue;
    // Le message prescrit la profondeur : x ≥ 100−reach (ou ≤ reach). On prend
    // 2 unités DEDANS pour ne pas coller au seuil, et on franchit la médiane
    // dans tous les cas — les deux défauts se réparent d'un même élan.
    const cible = goalX === 100 ? Math.max(52, 100 - reach + 2) : Math.min(48, reach - 2);
    if (Math.abs(cible - from.x) < 4) continue;      // déjà à portée, à la marge près
    const cx = Math.round(cible), cy = Math.round(from.y);
    // Le déplacement qui MÈNE à cette origine, s'il existe : on le prolonge.
    const mene = dernierMouv && Number.isFinite(+dernierMouv.tox)
      && Math.hypot(+dernierMouv.tox - from.x, +dernierMouv.toy - from.y) < 2 ? dernierMouv : null;
    if (mene) { mene.tox = cx; mene.toy = cy; if (mene.from) pos[mene.from] = { x: cx, y: cy }; }
    else if (a.from) {
      ajouts.push({ from: a.from, tox: cx, toy: cy, type: "course",
        style: "dashed", order: a.order - 0.5, label: "Course d'élan" });
      pos[a.from] = { x: cx, y: cy };
    } else continue;   // ni propriétaire ni course à prolonger : on ne devine pas
    // L'origine du tir REJOINT l'arrivée de la course, sinon la flèche partirait
    // toujours du même point et la règle continuerait de la signaler.
    a.fromx = cx; a.fromy = cy;
  }
  if (ajouts.length) scene.actions = actions.concat(ajouts);
  return scene;
}

/* ============================================================================
   ALLER CHERCHER LE VOLANT (correcteur de `frappe fantôme`)
   ----------------------------------------------------------------------------
   Le dépôt possédait déjà un correcteur de cette famille — `applyStrikeApproach`
   (analysis.js) — et il a été branché sur la route scène directe avec le reste
   du couple. MESURÉ sur 428 scènes IA capturées : il se déclenche 18 fois et
   ne répare AUCUNE des 16 frappes fantômes. La cause est un désalignement de
   prédicat : son garde exige que le TEXTE parle de déplacement/replacement
   (`/replac|déplacement|…/`), alors que la règle `frappe fantôme` ne regarde
   pas le texte — 11 des 16 scènes porteuses n'en disent pas un mot. Il exige
   aussi trois frappes ; la règle en juge deux.

   Cette fonction applique donc ce que le message de la règle prescrit mot pour
   mot — « déplacer X vers la retombée (course) avant sa frappe » — avec LE
   MÊME prédicat, LES MÊMES dérogations et LE MÊME seuil (22) que la règle. Elle
   ne peut pas s'exécuter sur une scène que la règle laisserait passer.

   BADMINTON SEULEMENT, comme la règle : sans rebond, le volant se frappe LÀ où
   il retombe. Au tennis et à la table, la balle REBONDIT puis poursuit vers le
   frappeur posté derrière — la distance retombée→frappeur y est normale.

   IDEMPOTENCE PAR CONSTRUCTION : la course insérée déplace l'acteur (suivi de
   positions), et l'origine explicite de la frappe est réalignée sur la
   retombée. Au second passage, la distance vaut 0 — la fonction ne peut pas
   se rejouer sur son propre résultat. */
export function enforceStrikeOrigin(scene, sport, text = "") {
  if (sport !== "badminton") return scene;
  const players = (Array.isArray(scene?.players) ? scene.players : [])
    .filter((p) => Number.isFinite(+p.x) && Number.isFinite(+p.y));
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const markers = Array.isArray(scene?.markers) ? scene.markers : [];
  if (!players.length || !actions.length) return scene;
  const t = String(text || "");
  // Les dérogations de la règle, recopiées telles quelles : distribution,
  // ateliers multi-volants, terrains dupliqués, et simple non strict (en
  // double, « chaque frappe répond à la précédente » n'a plus de sens).
  const waived = /pour (?:soi|lui|elle)[- ]?m?[êe]?m?e?|jongl|panier de balles|distributeur|casier|envoie successivement|distribue|multi.?(?:balles?|volants?)|chacun (?:son|sa)|face au mur|contre le mur/i.test(t);
  if (waived) return scene;
  const multi = players.some((p) => /_g\d/i.test(String(p.id)))
    || markers.filter((m) => ["ballon", "volant", "balle"].includes(String(m.type || "").toLowerCase())).length >= 2;
  if (multi) return scene;
  const perso = (p) => /observ|arbitre|enseign|coach/i.test(p.role || "");
  const fieldA = players.filter((p) => p.team === "A" && !perso(p));
  const fieldB = players.filter((p) => p.team === "B" && !perso(p));
  if (fieldA.length !== 1 || fieldB.length !== 1) return scene;
  if (actions.some((a) => a.type === "passe" || a.type === "dribble")) return scene;   // rallyOnly

  const byId = {}; players.forEach((p) => { byId[p.id] = p; });
  const pos = {};
  for (const p of players) pos[p.id] = { x: +p.x, y: +p.y };
  const ordered = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  const ajouts = [];
  let prev = null;
  for (const a of ordered) {
    if (a.type === "course" || a.type === "dribble") {
      if (a.from && a.to == null && Number.isFinite(+a.tox)) pos[a.from] = { x: +a.tox, y: +a.toy };
      continue;
    }
    if (!["renvoi", "tir"].includes(a.type)) continue;
    const origin = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : pos[a.from];
    // La règle n'examine la frappe fantôme QUE lorsque le frappeur CHANGE
    // (`else if` : deux frappes du même joueur relèvent de « frappes
    // enchaînées », un défaut d'alternance qu'une course ne répare pas).
    // …ET la retombée doit être DANS SON CAMP : on ne va chercher que le
    // volant qui tombe chez soi, sinon c'est LE JOUEUR qui franchit le filet.
    // Le camp se lit sur le POSTE DÉCLARÉ, comme le fait le juge de règlement
    // (`camp_filet` regarde `p.x`) et comme le fait `applyStrikeApproach`.
    // EXCEPTION mesurée : un frappeur posé SUR la ligne du filet (|x−50| < 2,5,
    // le seuil de `sur_filet`) n'a pas de camp déterminable — le bloquer
    // coûtait un cas (B11, dont le frappeur est à x=50 exactement).
    const poste = byId[a.from];
    const px = poste ? +poste.x : NaN;
    const sonCamp = !Number.isFinite(px) || Math.abs(px - 50) < 2.5
      || (px < 50) === (prev && prev.landing.x < 50);
    // …ET la frappe doit CONTINUER de franchir le filet une fois son origine
    // déplacée. Prédicat repris mot pour mot de `camp_filet` (regulation.js
    // § 3, filet à x=50, tolérance 6 et bande morte 2). Ce correcteur tourne
    // APRÈS `enforceRegulation` — s'il créait une frappe non franchissante,
    // plus personne ne la réparerait. C'est le précédent du service étagé
    // dans la rivière : un correcteur ne doit pas créer le défaut qu'un autre
    // répare.
    const tx = a.to == null && Number.isFinite(+a.tox) ? +a.tox : null;
    const franchitEncore = tx == null || prev == null
      || Math.abs(tx - prev.landing.x) < 6
      || !((prev.landing.x - 50) * (tx - 50) > 0
        && Math.abs(prev.landing.x - 50) > 2 && Math.abs(tx - 50) > 2);
    if (prev && sonCamp && franchitEncore && a.from != null && a.from !== prev.by && origin
      && Math.hypot(origin.x - prev.landing.x, origin.y - prev.landing.y) > 22) {
      const cible = { x: prev.landing.x, y: prev.landing.y };
      ajouts.push({ from: a.from, tox: cible.x, toy: cible.y, type: "course", style: "dashed",
        order: a.order - 0.5, label: "Déplacement sur le volant" });
      // L'origine EXPLICITE doit suivre, sinon la règle continue de lire
      // l'ancien point de départ malgré la course — et le correcteur ne serait
      // pas idempotent.
      if (Number.isFinite(+a.fromx)) { a.fromx = cible.x; a.fromy = cible.y; }
      pos[a.from] = cible;
    }
    const landing = a.to != null ? pos[a.to] : (Number.isFinite(+a.tox) ? { x: +a.tox, y: +a.toy } : null);
    if (landing) prev = { landing, by: a.from, order: a.order };
  }
  if (ajouts.length) scene.actions = actions.concat(ajouts);
  return scene;
}

/* ============================================================================
   FAIRE SE CROISER LES COURSES (correcteur de `croisement`)
   ----------------------------------------------------------------------------
   Classe désignée par le rejeu de contrôle du 13/08 (3 rejets sur la route qui
   sert, 6 sur les deux routes de repli), et la seule règle du dépôt dont le
   message PRESCRIT le geste sans que personne ne l'applique : « ÉCHANGER les
   points d'arrivée des deux coureurs ». Écrite le 04/08 depuis deux 👎 —
   PROD22 « il n'y a pas de courses croisées » et PROD32, où l'IA étiquetait
   « croise derrière A1 » deux droites RIGOUREUSEMENT PARALLÈLES : l'étiquette
   mentait, la géométrie non.

   Ce correcteur fait littéralement ce que le message dit, avec le déclencheur
   (`ASK_CROSS`), la lecture des courses (`crossRuns`, positions ÉVOLUÉES) et le
   test d'intersection (`segmentsCut`) DE LA RÈGLE ELLE-MÊME.

   IL NE PEUT PAS AGGRAVER LE VERDICT, par construction : il n'échange que si le
   test de la règle, rejoué sur les courses échangées, répond OUI. Une paire qui
   ne produit pas de croisement n'est pas touchée.

   CE QU'IL NE FAIT PAS, délibérément :
   • il ne touche que les arrivées en POINT (`tox`/`toy`). Une course qui vise un
     JOUEUR (« suit le ballon » du gabarit deux colonnes) dit à qui elle va :
     lui donner la destination d'un autre changerait le sens de la scène, pas sa
     géométrie. Mesuré : les 2 cas concernés sont un gabarit qui ne correspond
     déjà pas au texte — un autre chantier ;
   • il n'échange pas si l'une des deux courses devenait NULLE (< 6 unités) :
     `dropStillMoves` la supprimerait deux étages plus loin et le croisement
     disparaîtrait avec elle. C'est le défaut « deux correcteurs qui ne rangent
     pas le temps pareil », évité cette fois avant de le payer ;
   • il ne déplace AUCUN joueur et n'ajoute AUCUNE flèche : il permute deux
     destinations existantes.

   Faisabilité mesurée AVANT écriture (méthode de la session « flèche traverse »,
   dont le 151/151 avait autorisé le chantier) : sur les 6 scènes de repli qui
   échouent, 4 sont réparables par cet échange, et les trois bancs sortent
   PROPRES après (fidélité, règlement, lisibilité) — aucun défaut créé. */
export function enforceCrossedRuns(scene, sport, text = "") {
  if (!ASK_CROSS.test(coreSituationText(text))) return scene;
  const runs = crossRuns(scene);
  if (runs.length < 2 || crossedPair(runs)) return scene;   // déjà croisé : rien à faire
  const MIN = 6;                                            // seuil de `dropStillMoves`
  const dist = (P, Q) => Math.hypot(P.x - Q.x, P.y - Q.y);
  let best = null;
  for (let i = 0; i < runs.length; i++) for (let j = i + 1; j < runs.length; j++) {
    const u = runs[i], v = runs[j];
    if (u.id === v.id || u.team !== v.team) continue;
    if (u.versJoueur || v.versJoueur) continue;
    const su = { A: u.A, B: v.B }, sv = { A: v.A, B: u.B };
    if (!segmentsCut(su, sv)) continue;                     // le test DE LA RÈGLE
    const lmin = Math.min(dist(su.A, su.B), dist(sv.A, sv.B));
    if (lmin < MIN) continue;
    // La paire la plus LISIBLE gagne : deux courses franches valent mieux que
    // deux moignons qui se coupent. À égalité, la première dans l'ordre du jeu.
    if (!best || lmin > best.lmin) best = { u, v, lmin };
  }
  if (!best) return scene;
  const bu = { ...best.u.B }, bv = { ...best.v.B };
  best.u.act.tox = bv.x; best.u.act.toy = bv.y;
  best.v.act.tox = bu.x; best.v.act.toy = bu.y;
  return scene;
}
